/**
 * MVP-272 Phase 0. The source of truth for the KEY SET translated across every later phase — each
 * phase adds keys here (and to `messages.th.ts`) as it moves one surface's strings into the
 * dictionary, but the SHAPE of this file is decided now so nothing has to migrate later.
 *
 * Flat, not nested: every key is a single string with a `.`-separated namespace prefix per surface
 * (e.g. `settings.language.label`), never `{ settings: { language: { label: ... } } }`. A flat
 * object means `messages.th.ts`'s `Record<MessageKey, string>` IS the whole parity check — a
 * nested shape would need a recursive mapped type, and this repo's own per-field-fallback pattern
 * (`libraryPrefs.ts`/`vocalLevelsPrefs.ts`) has no clean equivalent once keys nest arbitrarily
 * deep.
 *
 * MVP-272 Phase 0 wired nothing into the UI (see that task's own "Change no user-visible copy")
 * and seeded five keys purely to prove two mechanisms: `messages.th.ts` failing to COMPILE on a
 * missing or extra key, and `{placeholder}` interpolation (`i18n/translate.ts`). The phases that
 * followed filled this file with real copy.
 *
 * MVP-406 swept what those five left behind. `common.cancel` and `common.retry` found real callers
 * and stay as ordinary copy. `common.ok` and `queue.positionCount` never did, and are deleted.
 * `greeting.welcome` has no caller either and stays ON PURPOSE, the only string in here that is not
 * copy: `i18n/i18n.test.ts` interpolates it against this real dictionary, which is still the only
 * proof that `{placeholder}` substitution works on the object the app actually ships. Deleting it
 * would mean that test asserting against a fixture it wrote itself.
 */
export const en = {
  'common.cancel': 'Cancel',
  'common.retry': 'Retry',
  /** MVP-406: not copy — the live interpolation fixture. See this file's header. */
  'greeting.welcome': 'Welcome, {name}',

  // MVP-293: the password reveal toggle in `ui/TextField.tsx`. It lives here, not with any one
  // screen's copy, because every password field in the app renders it — sign-in, the invite form,
  // the account dialog — and none of them owns it. This makes `TextField` the first `ui/` component
  // to take `t`; the six accessible names still hardcoded elsewhere in `ui/` are MVP-289.
  'field.revealPassword': 'Show password',
  'field.hidePassword': 'Hide password',

  // MVP-272 Phase 1 (guest request page, RequestPage.tsx). The passcode/closed/link-expired gate
  // states shown before the real search+request UI ever mounts.
  'guest.gate.wrongPasscode': 'That venue passcode is not right — ask the KJ.',
  'guest.gate.linkExpiredTitle': "This link isn't live anymore",
  'guest.gate.linkExpiredDetail': 'Ask the KJ for a fresh QR code or link.',
  'guest.gate.passcodeTitle': 'This session needs a passcode',
  'guest.gate.passcodeFallbackDescription': "Ask the KJ for tonight's venue passcode.",
  'guest.gate.passcodeLabel': 'Passcode',
  'guest.gate.continue': 'Continue',
  // MVP-471. Not yet owner-reviewed.
  'guest.gate.challengeTitle': 'Quick check first',
  'guest.gate.challengeDescription': 'Tap the box below to show you are not a robot. You only do this once tonight.',
  'guest.gate.challengeChecking': 'Checking…',
  'guest.gate.challengeUnavailable': 'The check could not load. Try turning off any ad blocker, or ask the KJ.',
  // MVP-269: the guest page's `<h1>`. Visually hidden, so it is written for someone hearing it
  // announced with no venue name or keypad in front of them — it says what the page is FOR, not
  // what it is called.
  'guest.pageTitle': 'Request a song',
  'guest.closed.title': 'Requests are closed right now',
  'guest.closed.fallbackDescription': 'Check back soon.',

  // MVP-274 R1 — GuestSearch.tsx's two doors, and GuestCodePad.tsx behind the first one. The code
  // is the number printed next to every song in the venue's book; "song number" rather than
  // "catalogue ID" because that is what a customer holding the book would call it.
  'guest.find.modeLabel': 'How to find your song',
  'guest.find.modeCode': 'Song number',
  'guest.find.modeSearch': 'Search',
  'guest.find.browseNote': 'Every song, by number. Tap one, or type its number above.',
  // MVP-451: browse loads on scroll now; this is the row that says so while it happens.
  'guest.find.loadingMore': 'Loading more songs…',
  'guest.code.hint': 'Type the number from the song book',
  'guest.code.enteredLabel': 'Song number entered',
  'guest.code.clear': 'Clear the number',
  'guest.code.backspace': 'Delete the last digit',
  'guest.code.noMatchTitle': 'No song number {code}',
  'guest.code.noMatchHint': 'Check the number in the book, or search by title instead.',
  'guest.code.emptyTitle': 'No songs to show',
  'guest.code.emptyHint': 'Type a song number above, or ask the KJ.',

  // GuestSearch.tsx — the sticky search bar + results list.
  'guest.search.label': 'Search for a song',
  'guest.search.placeholder': 'Search by title or artist',
  'guest.search.noMatchesTitle': 'No matches for "{query}"',
  'guest.search.emptyTitle': 'Search for a song',
  'guest.search.noMatchesHint': 'Try a different spelling or artist.',
  'guest.search.emptyHint': 'Type a title or an artist to get started.',
  'guest.search.clear': 'Clear search',
  // MVP-335: a visually-hidden aria-live="polite" region beside the result list — the results
  // region most likely to be read by a screen reader in this app, and until now it announced
  // nothing when a guest's typing (or a code digit, or a "Show more" tap) replaced the list.
  // Three keys, not one spliced sentence: `resultsAnnouncementNone`/`...OneFound` read as their own
  // sentences, and `...ManyFound` is the only one that carries `{count}` — the same one/many split
  // `libraryActions.ts`'s `describeBreakMusicAction` uses, for the same reason (see its comment).
  'guest.search.resultsAnnouncementNone': 'No songs found',
  'guest.search.resultsAnnouncementOneFound': '1 song found',
  'guest.search.resultsAnnouncementManyFound': '{count} songs found',

  // GuestSongRow.tsx / guestSongRowLabel.ts — one result row and its accessible name.
  'guest.songRow.unknownArtist': 'Unknown artist',
  // MVP-369: a fragment, not a sentence — it is appended to the request sentence below to finish
  // the row button's announced name, so it is lowercase and carries no punctuation of its own.
  'guest.songRow.catalogueCode': 'song number {code}',
  'guest.songRow.requestWithArtist': 'Request {title} by {artist}',
  'guest.songRow.requestTitleOnly': 'Request {title}',

  // QueuePositionCard.tsx — this device's own live request card.
  'guest.queue.songFallback': 'Your song',
  'guest.queue.askedPrefix': 'Asked {age}',
  'guest.queue.dismiss': 'Dismiss',

  // guestRefetch.ts's describeQueuePosition() — the status line under the song title on this
  // device's own request card, and also DUPLICATE_REQUEST's detail copy (requestErrors.ts), which
  // names exactly which state of a pre-existing request blocked a re-request.
  'guest.queue.pending': 'Waiting for the KJ to say yes.',
  'guest.queue.nowPlaying': "You're up — go sing! \u{1F3A4}",
  'guest.queue.inLine': '#{position} in line · {eta} to go',
  'guest.queue.acceptedFallback': 'Accepted — ask the KJ about your spot in the queue.',
  'guest.queue.played': 'Played — thanks for singing!',
  'guest.queue.rejectedWithReason': 'Not this time: {reason}',
  'guest.queue.rejectedFallback': 'Not this time — ask the KJ why.',
  'guest.queue.expired': 'This request expired before the KJ got to it — search again if you still want to sing it.',

  // guestRequestAge.ts's describeRequestAge() — the "Asked {age}" age on a terminal request card.
  // Deliberately duplicates lib/format.ts's formatRelativeAge() thresholds rather than importing
  // them (see that file's own header comment for why) — English values here must stay
  // byte-identical to formatRelativeAge()'s output.
  'guest.queue.age.unknown': '—',
  'guest.queue.age.justNow': 'just now',
  'guest.queue.age.minutes': '{n} min ago',
  'guest.queue.age.hours': '{n} h ago',
  'guest.queue.age.yesterday': 'yesterday',
  'guest.queue.age.days': '{n} days ago',

  // RateLimitNotice.tsx — the countdown shown under a 429/blocked error.
  'guest.rateLimit.tryAgainIn': 'Try again in {duration}',

  // RequestSheet.tsx — the bottom sheet a tap on a search result opens.
  'guest.sheet.titleFallback': 'Request this song',
  'guest.sheet.submit': 'Request this song',
  'guest.sheet.keyChangeLabel': 'Key change',
  'guest.sheet.keyChangeUnit': 'semitones',
  'guest.sheet.close': 'Close',
  'guest.sheet.keyChangeDown': 'Decrease key change',
  'guest.sheet.keyChangeUp': 'Increase key change',

  // SingerNameField.tsx
  'guest.singerName.label': 'Your name',
  'guest.singerName.placeholder': 'What should the KJ call you?',

  // requestErrors.ts — describeRequestError()'s title/detail copy, keyed by the server's own error
  // code (see that file's own doc comment for why `detail` is usually the server's own message
  // rather than app-authored copy — 'guest.errors.serverMessage' below is the identity passthrough
  // for exactly that case, so every RequestErrorCopy can be resolved the same way: `t(key, params)`).
  'guest.errors.codeRequired': 'Missing join code',
  'guest.errors.sessionNotFound': "We can't find a session for this code",
  'guest.errors.sessionNotFoundDetail': 'Check the code on the screen or ask the KJ.',
  'guest.errors.requestsClosed': 'Requests are closed',
  'guest.errors.passcodeInvalid': "That passcode isn't right",
  'guest.errors.songRequired': 'Pick a song first',
  'guest.errors.deviceIdRequired': "Couldn't identify this phone",
  'guest.errors.singerNameRequired': 'Enter your name',
  'guest.errors.singerNameInvalid': "That name won't work",
  'guest.errors.duplicateRequest': 'Already on the list',
  'guest.errors.deviceBlocked': "You've been blocked",
  'guest.errors.rateLimited': 'Slow down',
  'guest.errors.requestsFull': 'Requests are full right now',
  'guest.errors.networkError': "Can't reach the server",
  'guest.errors.genericTitle': "Couldn't send that",
  'guest.errors.genericDetail': 'Something interrupted the connection — check your signal and try again.',
  'guest.errors.tryAgainDetail': 'Try again in a moment.',
  'guest.errors.serverMessage': '{message}',
  'guest.errors.alreadyInQueue': 'Already in the queue',
  'guest.errors.duplicateNowPlaying': "You're up",
  'guest.errors.duplicateAccepted': 'Already accepted',
  'guest.errors.duplicatePlayed': 'Already sang this one',
  'guest.errors.duplicateAsked': 'Already asked',

  // MVP-272 Phase 2 (Stage — the audience display). Shared with no other surface unless the key
  // says `common.` — everything under `stage.` is read by a room full of people from across a bar,
  // not by an operator leaning into a monitor, and it is sized and worded for that.
  //
  // `common.` is for a string that is genuinely the same sentence on more than one surface. It
  // starts with exactly one member on purpose: a namespace that collects strings which merely
  // resemble each other is how a dictionary ends up with one key whose Thai has to satisfy three
  // different contexts at once.
  'common.unknownArtist': 'Unknown artist',
  // Gained its second and third caller in Phase 3b (`QueueRow`, `requestTray.ts`) and moved here
  // from `stage.attract.unknownSong`, which was its only one. Same sentence, three surfaces — the
  // exact case `common.` is for.
  'common.unknownSong': 'Unknown song',
  // MVP-318: `errorRecovery.ts`'s `mediaLookupErrorMessage` — read by the Stage's own hold card
  // (an audience TV) AND the Cockpit's local media-error toast (`usePlaybackEngine.ts`), which is
  // exactly the "same sentence, more than one surface" case `common.` exists for. Used to be a
  // finished English sentence built inline, with a 403-specific branch naming an HTTP status and
  // "role permissions" — fine for a KJ debugging, meaningless and never localized for a room full
  // of guests. One plain line, no status code, works for both.
  'common.mediaLookupFailed': 'Couldn\'t load media for "{title}".',

  'stage.documentTitle': 'Stage — RMS Karaoke',
  'stage.gesture': 'Click anywhere to start the stage',
  'stage.joinBadge': 'Request a song',
  'stage.paused': 'Paused',
  'stage.breakMusic': 'Break music',

  // StageRoot's <video> onError. The bare code is the browser's MediaError.code — meaningless to
  // the audience, but it is the only thing distinguishing four very different failures, and the KJ
  // reads this screen too when something goes wrong mid-song.
  'stage.mediaError': 'Playback error.',
  'stage.mediaErrorWithCode': 'Playback error (code {code}).',

  // StageHoldCard — what the audience sees when there is deliberately no picture.
  'stage.hold.errorHeadline': 'One moment',
  'stage.hold.errorDetail': 'Something interrupted playback.',
  'stage.hold.limbo': 'Getting the next song ready…',
  // MVP-318: `StageRoot.tsx`'s own "the server has no media file for this song at all" case —
  // used to be `"${title}" has no media file attached.`, built inline and never run through `t()`,
  // so it stayed English on a Thai Stage. The room does not need the technical reason (no media
  // relation vs. a failed lookup, `common.mediaLookupFailed` above); it needs to know the song
  // will not play.
  'stage.hold.mediaMissing': "This song can't play right now.",

  'stage.countdown.label': 'Up next in',
  'stage.upNext.label': 'Up next',

  // AttractScreen — the idle screen between sessions and between songs.
  'stage.attract.comingUp': 'Coming up',
  'stage.attract.unnamedSinger': 'Unnamed',
  'stage.attract.joinHeadline': 'Request a song from your phone',
  'stage.attract.noSessionHeadline': 'Stage is ready — start a session in the cockpit',

  'stage.marquee.announcement': 'Announcement',

  // MVP-272 Phase 3a (the cockpit's player transport bar). Read by ONE person — the KJ — standing
  // at a laptop in a dim room, mid-song, usually with a singer waiting. That is the opposite of the
  // `stage.` audience above and it is why nothing is shared between the two namespaces: the Stage
  // says "Paused" to a room, this bar says it to the person who paused it.
  //
  // The popover titles are SHOUTED in the source ('KEY (SEMITONES)') because `.player-popover__title`
  // is `text-label`, which is `text-transform: uppercase` — the caps live in CSS, so the English
  // here is written in the case it is authored in and Thai, which has no case, is unaffected.
  'player.resetToZero': 'Reset to 0',

  // MVP-375. The cockpit's control row — key, vocals, tempo, A-B, sync — is dead until a song is
  // loaded, which is exactly the state a new operator meets on first paint. One sentence for the
  // whole row rather than five identical tooltips: they all go dark for the same reason, and the
  // reason is about the session, not about any one control. DESIGN §6.2.
  // MVP-410: was "these controls", which claimed the whole row and meant five of the eight in it.
  // Break music, the output device and the link badge stay live with no song loaded, because they
  // are about the room and the night rather than the song. "The song controls" is the group's own
  // name one line below — the sentence and the label it describes now say the same words.
  'player.controls.noSong': 'Start a song from the rotation to use the song controls.',
  // MVP-398: the same row, the state before that one. `noSong` tells the operator to start a song
  // from the rotation, which is a contradiction when the rotation is empty — that state is why
  // MVP-388 silenced the whole sentence, leaving five dark controls unexplained on the cockpit's
  // first-paint state. This is what the row actually means then.
  'player.controls.noRotation': 'Add a singer to the rotation to use the song controls.',
  // MVP-384. The name of the group `player.controls.noSong` speaks for. A description on an unnamed
  // group is announced as a sentence attached to nothing; with a name, entering the group says
  // "Song controls — start a song from the rotation to use these controls", which is the sentence
  // arriving at the moment it is useful rather than only where the eye happens to land.
  'player.controls.groupLabel': 'Song controls',
  // MVP-558: the volume + output-device micro-group. Named for the room's audio path rather than
  // for its two members, so a third control joining it does not make the label a lie.
  'player.controls.audioPathLabel': 'Room audio path',

  // KeyControl. `player.key.trigger` is reused as NowPlayingCard's badge: same words, same meaning
  // ("this song is shifted"), one chip either way — a second key would be two Thai sentences to
  // keep in step for no difference the operator can see.
  'player.key.trigger': 'Key {value}',
  'player.key.ariaLabel': 'Key shift, currently {value}',
  'player.key.popoverTitle': 'KEY (SEMITONES)',
  'player.key.stepperLabel': 'Key shift',

  // TempoControl.
  'player.tempo.trigger': 'Tempo {value}',
  'player.tempo.ariaLabel': 'Tempo, currently {value}',
  'player.tempo.popoverTitle': 'TEMPO',
  'player.tempo.stepperLabel': 'Tempo',

  // MVP-413. The trigger shows the number, not a word: the whole point of this control is that a
  // console left at 30% used to be invisible, and a label reading "Volume" would keep it that way.
  'player.volume.trigger': 'Volume {value}%',
  'player.volume.triggerMuted': 'Muted',
  'player.volume.ariaLabel': 'Volume, currently {value} percent',
  'player.volume.ariaLabelMuted': 'Volume, currently muted',
  'player.volume.popoverTitle': 'VOLUME',
  'player.volume.stepperLabel': 'Volume',
  'player.volume.mute': 'Mute',
  'player.volume.unmute': 'Unmute',
  'player.volume.reset': 'Back to 100%',
  'player.volume.decrease': 'Volume down',
  'player.volume.increase': 'Volume up',

  // SyncOffsetControl. The hotkey hint names `[` and `]` literally — those are physical keys on the
  // operator's keyboard, so they stay as they are in every language.
  'player.sync.trigger': 'Sync {value}',
  'player.sync.popoverTitle': 'LYRIC / AUDIO SYNC OFFSET',
  'player.sync.stepperLabel': 'Sync offset',
  'player.sync.hotkeyHint': 'Hotkeys: [ / ] · nudges lyrics earlier/later, not the audio itself.',

  // VocalChannelControl. The three picks are the segmented control's own labels AND the word the
  // "guide vocal on ..." caption drops into, so they have to read as a channel name on their own.
  'player.vocal.groupLabel': 'Audio channel',
  'player.vocal.pickLeft': 'L',
  'player.vocal.pickRight': 'R',
  'player.vocal.pickBoth': 'Both',
  'player.vocal.guideVocalOn': 'Guide vocal on {side}',
  // MVP-657: the Cockpit's guide-vocal tag editor — admin only. The value is written to the song's
  // own record and wins over the scanner for ever, so the words say which song and how long it
  // lasts, not just which channel.
  'player.vocal.tagLabel': 'Guide vocal on this song',
  'player.vocal.tagUnknown': 'Not established',
  'player.vocal.tagLeft': 'Left channel',
  'player.vocal.tagRight': 'Right channel',
  'player.vocal.tagBoth': 'Both channels',
  'player.vocal.tagNone': 'No separate vocal channel',
  'player.vocal.tagSaved': 'Saved to this song',
  'player.vocal.tagError': 'Could not save the guide-vocal channel.',
  'player.vocal.leftLevel': 'Source-left channel level',
  'player.vocal.rightLevel': 'Source-right channel level',

  // MVP-499: auto-duck. Every one of these is a STATE the operator is standing in front of, so each
  // says what is true right now rather than naming the setting — "Auto-duck: on" would be the same
  // word for a mic that is listening and a mic the browser was refused. `noChannel` is the one that
  // earns its place: it is the difference between "this is broken" and "this song was never tagged".
  'player.autoDuck.off': 'Auto-duck off',
  'player.autoDuck.starting': 'Opening the mic…',
  'player.autoDuck.listening': 'Listening',
  'player.autoDuck.ducking': 'Lowering guide vocal',
  'player.autoDuck.noChannel': 'This song is not tagged',
  // MVP-664. Names the pick, not the feature: the fix is one button away in the row beside it.
  'player.autoDuck.isolated': 'Needs channel: Both',
  // MVP-675: a permission the operator can grant, not a broken mic — the string is the only place
  // that distinction is made.
  'player.autoDuck.denied': 'Microphone permission refused',
  // MVP-675: the condition is "no device the browser can see", which is not the claim that the PC
  // has none — a mic wired into a hardware mixer is exactly the room this feature was written for.
  'player.autoDuck.unavailable': 'No microphone found',
  // MVP-667. The Settings half. "Sensitivity" is phrased as how easily it REACTS, which is the
  // inverse of the margin it sets — see `DUCK_SENSITIVITY_MARGIN_DB`.
  'settings.autoDuck': 'Auto-duck',
  'settings.autoDuck.note':
    'Lowers the guide vocal while someone sings. Each PC can still switch it on or off from the cockpit, and that choice wins here.',
  'settings.autoDuck.enabledByDefault': 'On by default in every room',
  'settings.autoDuck.sensitivity': 'Sensitivity',
  'settings.autoDuck.sensitivityHigh': 'High — reacts to a quiet singer',
  'settings.autoDuck.sensitivityNormal': 'Normal',
  'settings.autoDuck.sensitivityLow': 'Low — only a loud singer',
  'settings.autoDuck.sensitivityNote':
    'Turn this down if the guide vocal dips when nobody is singing; turn it up if a quiet singer is not heard.',

  // MVP-669. A second axis, not more options on the first: sensitivity is WHEN, this is HOW FAR.
  'settings.autoDuck.depth': 'How far it lowers',
  'settings.autoDuck.depthLight': 'A little — the guide stays clearly audible',
  'settings.autoDuck.depthNormal': 'Normal',
  'settings.autoDuck.depthDeep': 'A lot — the guide almost disappears',
  'settings.autoDuck.depthNote':
    'The guide vocal is never switched off completely, so a wrongly tagged song can sound odd but never loses its backing track.',

  'settings.autoDuck.lift': 'How far the music comes up',
  'settings.autoDuck.liftOff': 'Not at all — only lower the guide',
  'settings.autoDuck.liftNormal': 'Normal',
  'settings.autoDuck.liftStrong': 'Strong — for a room with headroom to spare',
  'settings.autoDuck.liftNote':
    'While the guide vocal is lowered the other channel is raised, so the room does not simply get quieter when somebody sings. Choose "Strong" only where the PA has headroom left; on loud masters it can clip.',

  // MVP-675: says both halves of what the switch does. MVP-674 added the lift and no copy mentioned
  // it, so the hint described half the feature. The Settings rows stay about the duck alone — that
  // is what they configure.
  'player.autoDuck.hint':
    'Lowers the guide vocal and lifts the music while someone sings, for songs whose vocal channel is known. Needs a microphone plugged into this PC.',

  // ABRepeatControl. "A" and "B" are the loop's two points, named the same way every editor names
  // them — a letter, not a word, and not translated.
  'player.abRepeat.triggerLabel': 'A-B repeat',
  'player.abRepeat.popoverTitle': 'A–B REPEAT',
  'player.abRepeat.setA': 'Set A',
  'player.abRepeat.setAPending': 'Set A ({seconds}s)',
  'player.abRepeat.setB': 'Set B',
  'player.abRepeat.clear': 'Clear A-B loop',
  'player.abRepeat.looping': 'Looping {a}s – {b}s',
  'player.abRepeat.prewarming': 'Pre-warming the loop start before arming — one moment.',

  // ScrubBar. `valueText` is the seek slider's `aria-valuetext`; it is the only spoken form of the
  // position, so it says "seconds" in words rather than borrowing the visible clock's `m:ss`.
  'player.scrub.seek': 'Seek',
  'player.scrub.valueText': '{position} of {duration} seconds',
  'player.scrub.markerA': 'A: {seconds}s',
  'player.scrub.markerB': 'B: {seconds}s',

  // NowPlayingCard's badge row — only the controls currently away from their default.
  'player.badge.breakMusic': 'Break music',
  'player.badge.leftOnly': 'Left channel only',
  'player.badge.rightOnly': 'Right channel only',

  // BufferingCard, and the three labels `usePlaybackEngine` hands it. Those three used to be built
  // as finished sentences inside the hook and then re-identified by `label.startsWith('Up next')`;
  // the key IS that identity now (`BufferingInfo.labelKey`), so a translation can never change which
  // buffering state the engine thinks it is in.
  'player.buffering.buffering': 'Buffering…',
  'player.buffering.retrying': 'Playback hiccup, retrying ({attempt})…',
  'player.buffering.upNext': 'Up next: {title} — buffering…',
  'player.buffering.rate': '{mbps} Mbps',
  'player.buffering.eta': '~{seconds}s left',

  // MVP-288: the Cockpit's own local media-error toast (`usePlaybackEngine.ts`'s `onError`) — never
  // persisted. What DOES get persisted is `errorCode` alone; a Stage window renders that through its
  // own `t('stage.mediaErrorWithCode', ...)` (StageHoldCard.tsx), not this sentence.
  'player.error.playFailed': 'Couldn\'t play "{title}": {reason}.',
  'player.error.unknownTrack': 'this track',
  /** MVP-389: the end-of-track fallback when this hook is used standalone with nothing next. It
   * used to print the raw machine token — `decode-error — no next item queued.` — at the KJ. */
  'player.error.advanceNothingQueued': '{reason} — nothing else is queued.',
  // The media-lookup effect's success branch (song loaded, but its `media` relation is empty) —
  // not a caught error, so `apiErrorMessage` doesn't apply; this is the plain business message.
  'player.error.noMediaFile': '"{title}" has no media file attached. Ask the KJ to re-ingest this song.',
  // MVP-344: the banner sentence for `usePlaybackEngine`'s two `setError` sinks
  // (`OperatorConsole.tsx:862`). Those catches see a `DOMException` from the media element, whose
  // own `message` is an English browser string aimed at a developer console — nothing the KJ can
  // read or act on, so it never reaches them. What they can act on is stated here instead.
  'player.error.playbackFailed': "Couldn't start this song. Try playing it again.",
  // The `{reason}` half. These were machine tokens straight out of `errorRecovery.ts` until the
  // sentence around them started going through `t()`, at which point a Thai console read
  // `เล่น "…" ไม่ได้: decode-error` — translated everywhere except the word carrying the meaning.
  // Written as what the KJ can do about it where there is anything to do; the file/network split is
  // the one distinction that changes their next move.
  'player.error.reason.aborted': 'playback was interrupted',
  'player.error.reason.network': 'the connection dropped',
  'player.error.reason.decode': 'the file is damaged',
  'player.error.reason.unsupported': 'this format cannot be played',
  'player.error.reason.notFound': 'the file is missing',
  'player.error.reason.serverError': 'the media server did not respond',
  'player.error.reason.other': 'an unknown problem',
  // MVP-290: this used to be a finished sentence returned straight from `mediaFailureReason`
  // ("blocked by the media CDN's CORS policy (see DEPLOY.md)..."), the one case MVP-288 left
  // deliberately untranslated. It is a token like the others now; the DEPLOY.md pointer is aimed
  // at whoever configures the CDN, not the KJ reading this toast, so it is not repeated here.
  'player.error.reason.corsBlocked': "the media server isn't set up correctly",

  // MVP-272 Phase 3b (the cockpit's middle column: the rotation panel and the request tray).
  //
  // `rotation.`, not `queue.`: the guest's own `guest.queue.*` copy is about ONE person's own
  // request seen from a phone, and this is the KJ's view of everybody's turn. They are different
  // sentences about different things and must never be made to share a key.
  //
  // Most of what follows is a FAILURE. Every write in this panel goes straight to the server with
  // no optimistic overlay, so "couldn't do X" is the panel's normal vocabulary, and each one names
  // the specific thing that failed rather than saying "something went wrong" — the KJ has to decide
  // whether to retry or work around it, mid-set, in front of the room.
  'rotation.title': 'Rotation',
  'rotation.unknownSinger': 'Unknown singer',
  'rotation.thisSinger': 'this singer',
  'rotation.thisSong': 'this song',

  'rotation.badge.playing': 'Playing',
  'rotation.badge.paused': 'Paused',
  'rotation.badge.upNext': 'Up next',
  'rotation.badge.loading': 'Loading…',

  'rotation.undo': 'Undo',
  'rotation.redo': 'Redo',
  'rotation.undoLabel': 'Undo last change',
  'rotation.undoNothing': 'Nothing to undo',
  'rotation.redoLabel': 'Redo last undone change',
  'rotation.redoNothing': 'Nothing to redo',
  'rotation.undid': 'Undid: {op}',
  'rotation.redid': 'Redid: {op}',

  /** MVP-387: the rotation's panel-wide busy line. Not `common.loading` — nothing is being fetched,
   * a change the KJ just made is being written, and the two read differently at a glance. */
  'rotation.working': 'Saving…',

  'rotation.startNext': 'Start next',
  // MVP-399: the cockpit's own door into the rotation. "Add singer", not "Add to rotation" —
  // the panel is already titled with the rotation, and the KJ is naming a person, not a place.
  'rotation.addSingerLabel': 'Add singer',
  'rotation.addSingerTitle': 'Add a singer to the rotation',
  'rotation.addedSinger': 'Added {name}',
  'rotation.startNextNoPlayback': 'Not connected to playback yet',
  'rotation.startNextNothingQueued': 'Nothing queued',

  // `staleAge` gives the m:ss; the sentence around it, and the server's own message in brackets,
  // are this key's.
  'rotation.stale': 'The rotation may be out of date — updated {age} ago, couldn\'t refresh it just now ({error}).',
  // How every `runMutation` reason below is joined to the server's message. Its own key because the
  // separator between "what I was doing" and "what the server said" is a punctuation decision, and
  // Thai does not necessarily want a colon-space either.
  'rotation.errorDetail': '{reason}: {message}',

  'rotation.loadFailedTitle': "Couldn't load the rotation",
  'rotation.notLoadedTitle': "Rotation isn't loaded",
  'rotation.notLoadedDescription': "Couldn't read tonight's rotation.",
  'rotation.emptyTitle': 'Nothing in the rotation',
  'rotation.noSongsQueued': 'No songs queued.',

  // MVP-550: finding one person in tonight's rotation, with that person standing at the KJ's elbow.
  // "Find a singer", not "Search" — the box narrows a list already on screen, it does not go looking
  // anywhere, and the rotation's own walk-up dialog is the one that searches the whole directory.
  'rotation.searchLabel': 'Find a singer in the rotation',
  'rotation.searchPlaceholder': 'Singer name',
  'rotation.searchClear': 'Clear the name filter',
  'rotation.searchNoMatchTitle': 'Nobody in the rotation matches “{query}”',
  // The sticky "now playing" card cannot be in a list that filtered it out, so this line takes its
  // place — the one fact the KJ must not lose track of while looking for someone else.
  'rotation.filterHidesPlaying': '{name} is singing now — your search is hiding their card.',

  // MVP-364: these two used to be `title` tooltips, which are the only thing carrying the meaning
  // of two bare numbers in the slot header. A tooltip never appears on touch — and the operator's
  // tablet is a first-class client (SPEC AC-Q-14) — so they are now the VisuallyHidden name beside
  // a short visible label. The long form stays the assistive-tech name; the short form is what a
  // sighted operator reads at a glance without having to hover anything.
  'rotation.songsThisRotation': 'Songs sung this rotation',
  'rotation.songsFactLabel': 'Songs',
  'rotation.etaHint': "Estimated time until this singer's turn",
  'rotation.etaFactLabel': 'Turn in',
  'rotation.pauseSinger': 'Pause {name}',
  'rotation.resumeSinger': 'Resume {name}',
  'rotation.moveSlotUp': 'Move slot up',
  'rotation.moveSlotDown': 'Move slot down',
  'rotation.removeSinger': 'Remove singer from rotation',
  'rotation.removedSinger': 'Removed {name}',
  'rotation.removedSong': 'Removed “{title}”',
  // MVP-412 (SPEC AC-Q-5). "Regular" is the word SPEC and OpenKJ both use, and the toggle's name
  // has to say what it DOES, not what it is called — a KJ meeting the heart for the first time
  // should not have to guess whether it favourites the singer, the song, or the night.
  'rotation.markRegular': 'Remember {name}’s list for future nights',
  'rotation.unmarkRegular': 'Stop remembering {name}’s list',
  'rotation.markedRegular': 'Remembering {name}’s list',
  'rotation.unmarkedRegular': 'No longer remembering {name}’s list',

  'rotation.error.undo': "Couldn't undo",
  'rotation.error.redo': "Couldn't redo",
  'rotation.error.reorderRotation': "Couldn't reorder the rotation",
  'rotation.error.reorderSongs': "Couldn't reorder this singer's songs",
  'rotation.error.pauseSinger': "Couldn't pause {name}",
  'rotation.error.resumeSinger': "Couldn't resume {name}",
  'rotation.error.removeSinger': "Couldn't remove {name}",
  'rotation.error.markRegular': "Couldn't start remembering {name}'s list",
  'rotation.error.unmarkRegular': "Couldn't stop remembering {name}'s list",
  'rotation.error.addSinger': "Couldn't add {name}",
  'rotation.error.removeSong': 'Couldn\'t remove "{title}"',
  'rotation.error.keyShift': "Couldn't change the key",
  'rotation.error.playNow': 'Couldn\'t play "{title}" now',
  'rotation.error.startNext': "Couldn't start the next song",

  // opLabels.ts — the past-tense name of the operation an Undo toast just reversed, so the toast
  // reads "Undid: Removed singer". Several ops share one label on purpose: a KJ does not care
  // whether a reorder moved a slot or an entry, only that a reorder was undone.
  'rotation.op.addSong': 'Added song',
  'rotation.op.removeSong': 'Removed song',
  'rotation.op.addSinger': 'Added singer',
  'rotation.op.removeSinger': 'Removed singer',
  'rotation.op.reordered': 'Reordered',
  'rotation.op.updateSinger': 'Updated singer',
  'rotation.op.advanced': 'Advanced rotation',

  // QueueRow — one queued song under a singer's slot.
  'rotation.row.moveUp': 'Move up',
  'rotation.row.moveDown': 'Move down',
  'rotation.row.playNow': 'Play now',
  'rotation.row.remove': 'Remove from queue',
  // DESIGN.md §6.2: never a disabled control with no stated reason. The reason is appended to the
  // control's own accessible name, so a screen-reader user hears why before they try it.
  'rotation.row.playNowDisabled': 'Play now — {reason}',
  'rotation.row.removeDisabled': 'Remove from queue — {reason}',
  'rotation.reason.currentlyPlaying': 'currently playing',
  'rotation.reason.alreadyPlaying': 'already playing',
  'rotation.reason.noPlayback': 'not connected to playback yet',
  'rotation.reason.unavailable': 'not available',

  // TonightHistoryDialog (MVP-414, AC-Q-11) — what was sung, per singer, re-queueable.
  //
  // MVP-420: every one of these said "tonight", and none of them could. `GET /api/session/summary`
  // returns the whole active SESSION with no date bound, and this venue leaves one session record
  // open across nights — so on 13 Sep the dialog listed performances from 3, 4, 5, 6 and 8 Sep
  // under the heading "Sung tonight". The data is the session; the copy now says so, and
  // `history.since` prints the span the list actually covers.
  'history.open': 'Session history',
  'history.title': 'Sung this session',
  'history.loading': 'Loading session history',
  'history.loadFailed': 'Could not load session history',
  'history.emptyTitle': 'Nothing sung yet this session',
  'history.since': 'Since {date}',
  'history.songCount': '{count} songs',
  'history.key': 'Key {key}',
  'history.cut': 'Cut',
  'history.requeue': 'Queue again',
  // MVP-426: a row whose song or singer was hard-deleted. It used to render as a timestamp, an
  // empty cell and a permanently disabled button — four of seven rows in live data — which reads
  // as broken rather than as gone.
  'history.songGone': 'Song deleted',
  'history.requeueGone': 'Cannot queue again',
  'history.requeued': '{song} queued again for {name}',
  'history.error.requeue': 'Could not queue {song} again',
  'history.exportCsv': 'Download CSV',
  // MVP-498: the dialog grew a second half. `play-history` answers what was SUNG; nothing on any
  // screen answered what was ASKED FOR — who requested what, and what the KJ did about it — even
  // though `GET /api/requests?status=all` has always been able to say. Both halves are scoped to
  // the room this screen is driving, which is the other half of MVP-498: the two endpoints behind
  // this dialog were resolving the active session with no room filter at all.
  'history.tabs': 'History for this room',
  'history.tab.sung': 'Sung',
  'history.tab.requests': 'Requests',
  'history.request.loading': 'Loading this room’s requests',
  'history.request.loadFailed': 'Could not load this room’s requests',
  'history.request.emptyTitle': 'Nothing requested yet this session',
  'history.request.by': 'Requested by {name}',
  'history.request.songGone': 'Song deleted',
  'history.request.note': 'Note: {note}',
  'history.request.rejectReason': 'Reason: {reason}',
  'history.request.outcome.pending': 'Waiting',
  'history.request.outcome.accepted': 'Accepted',
  'history.request.outcome.rejected': 'Rejected',
  // Swept by `expireStalePending`, not refused by anyone — a distinction the KJ needs when a guest
  // asks why their song was turned down.
  'history.request.outcome.expired': 'Expired',

  // MVP-498 slice 2 — the room move. Thai is the UI's default language and these are the English
  // switchable copies; the Thai side carries the owner-approved glossary words (ย้ายห้อง /
  // ย้ายประวัติไปห้องอื่น) and the review notes.
  'ops.roomMove.action': 'Move room',
  'ops.roomMove.heading': 'Move this night to another room',
  // Owner decision 2, said before the operator commits rather than discovered afterwards:
  // `playback-state` belongs to the room, and the room is not what moved.
  'ops.roomMove.confirmBody':
    'Every request and everything sung this session moves to {room}. Guests keep the same join code — but whatever is playing in the room you are leaving stops immediately.',
  'ops.roomMove.targetLabel': 'Move to room',
  // MVP-599: the picker's empty first option. A destination the operator never chose must not be
  // sitting in the box under a button that moves a live room, so the empty state needs words.
  'ops.roomMove.choose': 'Choose a room…',
  'ops.roomMove.confirm': 'Move',
  'ops.roomMove.done': 'Moved to {room}',
  // MVP-606. A branch with one room is an ordinary shop; rooms in the customer's other branches are
  // deliberately not offered, because a night cannot change buildings (server 409
  // ROOM_VENUE_MISMATCH).
  'ops.roomMove.noTargets': 'No other room in this branch to move to.',
  'ops.roomMove.error.busy': 'That room already has a night running. End it first, then move.',
  'ops.roomMove.error.roomGone': 'That room no longer exists.',
  'ops.roomMove.error.failed': 'Could not move the room. Please try again.',
  // MVP-647. Three refusals that used to read as `failed` ("please try again") over conditions a
  // retry refuses again for the same reason. The first two mean the other console got there first,
  // so they say look, not retry — and `runMove` refetches the session on both, because a sentence
  // telling somebody the screen is stale while the screen stays stale is half a fix.
  'ops.roomMove.error.sessionMoved': 'Someone else moved this night to another room just now. The screen has been refreshed — check where it is before moving it again.',
  'ops.roomMove.error.sessionEnded': 'This night has already ended, so there is nothing to move. The screen has been refreshed.',
  // Named in this panel's own docblocks since MVP-606 and still had no branch until MVP-647.
  'ops.roomMove.error.venueMismatch': 'That room is in another branch, and a night cannot move between branches. Pick a room in this branch.',

  // MVP-642 — rotating a leaked join code. Thai is the UI's default language and these are the
  // English switchable copies; the Thai side carries the review notes.
  'ops.joinCode.heading': 'Change the join code',
  'ops.joinCode.action': 'Change code',
  'ops.joinCode.current': 'Current code',
  // Says when to use it: a code that leaked is the only occasion for this button.
  'ops.joinCode.note': 'Use this if the code has got out — someone photographed the QR, or a printed code card was left on a table.',
  // The consequence before the operator commits, said as what happens to the guests. There is no
  // undo: the old code is stored nowhere, not on the session row and not in the audit row.
  'ops.joinCode.confirmBody':
    'Code {code} stops working immediately. Guests who have not joined yet must scan the new QR or type the new code. Printed cards and any photographed QR stop working too, and this cannot be undone. Guests who already joined stay in the rotation.',
  'ops.joinCode.confirm': 'Change it',
  'ops.joinCode.done': 'New join code is {code}',
  // One sentence per refusal the route can answer — `features/ops/joinCodeRotate.ts` maps each to
  // its server condition. None may be folded into `failed` (the MVP-641/644 defect).
  'ops.joinCode.error.sessionGone': 'This session has ended, so there is no code to change. Start a new session.',
  'ops.joinCode.error.forbidden': 'This account is not allowed to change the join code. Ask the shop owner.',
  'ops.joinCode.error.signedOut': 'Your sign-in has expired. Sign in again and retry.',
  'ops.joinCode.error.failed': 'Could not change the code. The old code still works. Please try again.',
  // Owner decision 3: the guest is told, in one line, rather than silently reconnected.
  'guest.roomMoved.notice': 'You have moved rooms. Reconnecting — your code still works.',

  // RequestTray — the accept/reject moderation list, shown only while `requestMode` is `review`.
  'requests.title': 'Requests',
  'requests.pendingCount': '{count} pending',
  'requests.emptyTitle': 'No requests yet',
  'requests.emptyDescription': 'Guests who scan the join QR land here.',
  'requests.accept': 'Accept',
  'requests.reject': 'Reject',
  // MVP-557 — the Thai is the primary copy; these are the switchable English mirrors.
  'requests.wait.justNow': 'just sent',
  'requests.wait.minutes': 'waiting {minutes} min',
  'requests.wait.hours': 'waiting {hours} hr',
  'requests.wait.hoursMinutes': 'waiting {hours} hr {minutes} min',
  'requests.oldest': 'longest wait',
  'requests.keyShift': 'Key {value}',

  // DESIGN.md §6.4's ONE connection language, for the whole app. `common.`, and genuinely so: the
  // guest's own card (`QueuePositionCard`, Phase 1) and the cockpit's status-bar indicator
  // (`ConnectionIndicator`) show the same four states in the same words on purpose — MVP-11 #16
  // made the guest page stop inventing its own phrasing, and two keys would let it drift back.
  //
  // Deferred out of Phase 1 and again out of Phase 3b's first commit for one reason: `connectionLabel`
  // is a pure `.ts` module tested under node, so it cannot call `t()`. It returns keys now, and
  // every caller renders them.
  //
  // "Silence is the reward for working" — `common.connection.connected` is the tooltip on an
  // otherwise wordless dot, never a banner.
  'common.connection.connected': 'Connected',
  'common.connection.reconnecting': 'Live updates reconnecting…',
  'common.connection.reconnectingDetail': 'Actions still work — the queue and rotation will catch up once the connection returns.',
  'common.connection.reauth': 'Signing back in…',
  'common.connection.offline': 'Offline',
  // MVP-11 #16: this app does not disable controls app-wide while offline (each panel surfaces its
  // own write failure), so the copy must not claim a disabled state that does not exist. There is a
  // test asserting the word "disabled" never appears here.
  'common.connection.offlineDetail': 'Actions may not save until the connection returns.',
  // The two things the indicator appends after the label. §6.4 reserves naming the exact failing
  // host for a genuine REST-reachability failure — never for a merely-reconnecting SSE stream.
  'common.connection.hostUnreachable': '{host} unreachable',
  'common.connection.updatedAgo': 'updated {age} ago',
  // The dot carries the state visually; this is the same state spelled out for a screen reader, so
  // it names what the value IS rather than leaving a bare word floating in the reading order.
  'common.connection.srStatus': 'Connection: {status}',

  // MVP-483. The same four-state language as `common.connection.*` above, said by the three
  // surfaces that had been quietly showing stale data instead: the Stage, the cockpit strip and the
  // guest phone. Deliberately one `common.outage.*` block rather than `stage.`/`ops.`/`guest.`
  // prefixes — the whole point of the task is that an outage reads the same everywhere, and three
  // namespaces is how the guest page drifted into its own phrasing before MVP-11 #16.
  //
  // Nothing here offers to queue or replay anything, because nothing does: there is no offline
  // write queue in this app and none was added. The copy says "stale", never "saved".
  'common.outage.stagePill': 'Reconnecting',
  // The Stage veil's one sentence. No error code, no host, no button — the audience cannot act on
  // any of them, and the KJ reads the cockpit, not this screen.
  'common.outage.stageVeil': 'Reconnecting to the server — this will carry on in a moment.',
  // The cockpit strip's second line, under `common.connection.offline`. The honest claim: the
  // screen is not being refreshed, so do not trust what is on it.
  'common.outage.stale': "What's on screen may not be up to date.",
  // The SSE-down-but-REST-alive notice, rendered inline beside the region it affects. It names the
  // poll interval on purpose: "live updates are gone" without "the list still refreshes" is what
  // makes a KJ think the cockpit has stopped working.
  'common.outage.liveRegion': 'Live updates paused here — this still refreshes every 15 seconds.',
  'common.outage.guestTitle': "Can't reach the venue right now",
  'common.outage.guestBody': "Your phone can't reach the server, so what's on screen may not be up to date. Stay on this page — it comes back on its own.",
  'common.outage.guestRetry': 'Try again',
  'common.outage.guestRetrying': 'Trying…',
  'common.outage.guestRetryFailed': 'Still no connection — wait a moment and try again.',

  // MVP-272 Phase 3c-1 (the console shell: the rail, the header, and the two panels that stand in
  // front of the cockpit before it can open). Phase 3 is split by surface — 3a transport, 3b queue,
  // 3c shell — and 3c is split again around `OperatorConsole.tsx`, which is 54K on its own.
  //
  // The rail's six entries. One word each, and they are read by a KJ mid-shift glancing at a 56px
  // icon strip, so the Thai is the short venue word rather than the literal translation.
  'nav.library': 'Library',
  'nav.cockpit': 'Cockpit',
  'nav.settings': 'Settings',
  'nav.ingest': 'Ingest',
  'nav.accounts': 'Accounts',
  'nav.activity': 'Activity',
  // MVP-358: the trash for the KJ app. `Withheld` rather than `Trash` — a hidden song was never
  // thrown away in the first place, and the two live on one list here.
  'nav.withheld': 'Withheld',

  // App.tsx's own shell chrome.
  'app.skipToContent': 'Skip to content',
  'app.manual': 'Manual',
'app.signOut': 'Sign out',
  // The fallback when a "Play now" write fails and the server said nothing useful. The server's own
  // message is preferred when there is one — that sentence is still English, and localising it is a
  // backend contract change (same class of problem as MVP-288).
  'app.playbackStartFailed': 'Could not start playback.',
  // MVP-335: announced via a visually-hidden aria-live region whenever `effectiveView` changes —
  // switching rail tabs used to leave a screen-reader user with no signal the screen had changed at
  // all (confirmed: no focus move, no announcement, tabbing just resumed wherever the old view left
  // off). `{view}` is `currentViewLabel` above it, so this can never name a screen the rail itself
  // does not.
  'app.viewChangedAnnouncement': 'Now showing {view}',
  // MVP-353: the mini-transport bar's own landmark name (`role="region"`) — MiniTransportBar.tsx,
  // visible on every Console tab except Cockpit itself, which already has the full transport bar.
  'app.miniTransport.regionLabel': 'Mini playback controls',

  // SessionStartPanel.tsx — the "no session running" wall in front of the whole cockpit.
  'ops.session.noneTitle': 'No session running',
  'ops.session.noneDescription': "Tonight's rotation, join code and QR all live on a session. Start one to open the cockpit.",
  'ops.session.start': "Start tonight's session",
  'ops.session.startFailed': "Couldn't start a session — {reason}.",
  'ops.session.startFailedFallback': 'request failed',

  // StageDisplayDialog.tsx (MVP-251, multi-select since MVP-285) — which monitor(s) the Stage
  // opens on.
  'ops.stageDisplay.title': 'Which displays are Stages?',
  'ops.stageDisplay.hint': 'Each display you pick gets its own Stage window, filling it completely. Remembered for next time, on this machine.',
  // The list's accessible name. MVP-285: plural — the list is a set of checkboxes now, and a screen
  // reader announcing "Display for the Stage window" over a multi-select is simply describing
  // something that stopped being true.
  'ops.stageDisplay.groupLabel': 'Displays to use as Stages',
  'ops.stageDisplay.primaryBadge': 'Primary',
  'ops.stageDisplay.rememberedBadge': 'Remembered',
  'ops.stageDisplay.confirm': 'Open Stage',
  // Only used when the OS itself gives a monitor no name. A real label ("DELL U2720Q") comes from
  // the Window Management API and is passed through untranslated — it is the sticker on the front
  // of the monitor, not our copy.
  'ops.stageDisplay.unnamedPrimary': 'Primary display',
  'ops.stageDisplay.unnamedSecondary': 'Secondary display',

  // MVP-272 Phase 3c-2 (the cockpit itself: OperatorConsole.tsx and the four small panels it is the
  // only host of). The tail every failed write in this app shares when the server said nothing
  // useful — one key, so the six sentences below cannot drift apart.
  'common.requestFailed': 'request failed',

  // OperatorConsole.tsx — the request tray's two write failures.
  'ops.request.acceptFailed': "Couldn't accept this request — {reason}.",
  'ops.request.rejectFailed': "Couldn't reject this request — {reason}.",
  // MVP-329: the reject undo. The toast names the guest and the song deliberately — a KJ
  // moderating four requests in a row needs to know WHICH one they just turned down.
  'ops.request.rejected': 'Rejected {singer}’s “{title}”',
  // MVP-546: the matching receipt for the constructive half. Accepting was the only one of the two
  // that said nothing at all.
  'ops.request.accepted': 'Added {singer}’s “{title}” to the queue',
  'ops.request.thisGuest': 'this guest',
  'ops.request.restoreFailed': "Couldn't undo that rejection — {reason}.",
  'ops.request.restoreTooOld': 'the request had already timed out, so it would expire again straight away',
  'ops.request.restoreNotRestorable': 'it is no longer rejected — someone else may have decided it',
  // MVP-556: the accept undo. `restoreTooOld` is shared with the reject undo on purpose — the
  // server returns the same `REQUEST_TOO_OLD_TO_RESTORE` for the same reason on both paths.
  'ops.request.unacceptFailed': "Couldn't undo that accept — {reason}.",
  'ops.request.unacceptTooLate': 'the song has already started or been sung, so it stays in the queue',
  'ops.request.unacceptNotAccepted': 'it is no longer accepted — someone else may have changed it',

  // The rotation hand-off (`cueFromRotationMeta`/`advance`/`playNow`). These sentences no longer
  // interpolate the caller's `reason`: that argument is a DIAGNOSTIC breadcrumb from the engine
  // ('decode-error', 'ended', 'Next pressed'), and dropping a code where a sentence goes read badly
  // in English and would have been an untranslatable English fragment inside Thai copy. The
  // server's own message is still preferred wherever there is one.
  'ops.error.startNextFailed': 'Couldn\'t start "{title}".',
  'ops.error.theNextSong': 'the next song',
  'ops.error.closeOutFailed': "Couldn't close out the last song.",
  'ops.error.advanceFailed': "Couldn't advance the rotation — {reason}.",
  'ops.error.reorderFailed': 'Couldn\'t reorder the rotation to play "{title}" — {reason}.',
  'ops.error.playNowFailed': 'Couldn\'t play "{title}" now — {reason}.',
  'ops.error.thisSong': 'this song',
  'ops.error.undoFailed': "Couldn't undo — {reason}.",
  'ops.error.cutUndoFailed': "Couldn't put that song back on — {reason}.",
  // MVP-376: the undo stack moved under the receipt (another station wrote, or a mutation this
  // client never saw). Nothing was changed — say so, because the KJ's next move depends on it.
  'ops.error.cutUndoForeign': "Didn't undo — the rotation changed since that song was stopped. Nothing was altered.",
  // Appended to whichever error is showing while the rate-limit lockout is in force.
  'ops.error.controlsLocked': 'Controls are locked for {seconds}s.',
  /** MVP-389: `describeControlWriteError`'s three sentences, which it used to build in English in a
   * plain `.ts` module and hand straight to this same banner. */
  'ops.error.rateLimitedRetry': 'Too many requests — try again in {seconds}s.',
  'ops.error.rateLimited': 'Too many requests — slow down and try again.',
  'ops.error.controlWriteFailed': 'That change did not go through. Try again.',

  // MVP-370: the receipt for a cut performance, and the way back out of it.
  'ops.toast.cutWithSinger': 'Stopped “{title}” — {singer} was singing',
  'ops.toast.cutTitleOnly': 'Stopped “{title}”',
  'ops.toast.cutUnnamed': 'Stopped the song that was playing',
  'ops.toast.cutUndoDetail': 'Undo puts it back on — it starts again from the beginning.',
  'ops.toast.cutUndoAction': 'Undo',
  'ops.toast.undone': 'Undone',
  'ops.toast.stageMovedTitle': 'Stage moved to the main display',
  'ops.toast.stageMovedDetail': 'Its own display was disconnected.',

  // The two size toggles over the preview video, and the gesture overlay the autoplay policy forces.
  'ops.video.wide': 'Wide video',
  'ops.video.exitWide': 'Exit wide video',
  'ops.video.fullscreen': 'Fullscreen video',
  'ops.video.exitFullscreen': 'Exit fullscreen video',
  'ops.video.resume': 'Click to resume playback',
  'ops.video.hidePreview': 'Hide video preview',
  'ops.video.showPreview': 'Show video preview',

  'ops.nothingPlayingTitle': 'Nothing playing',
  'ops.nothingPlayingDescription': 'Queue a song from the library, then Start next.',

  // The side panel's header and the Stage button, whose label depends on whether the window is up.
  // MVP-676. The cockpit's right side panel — the owner's own name for the column.
  'ops.rightSidePanel.hide': 'Hide the right side panel',
  'ops.rightSidePanel.show': 'Show the right side panel',
  'ops.rightSidePanel.joinCode': 'Join code',
  'ops.thisSession': 'This session',
  'ops.stage.open': 'Open Stage',
  'ops.stage.focus': 'Focus Stage',

  // MVP-500: storefront mode — the header button that enters it, the strip that says it is on, and
  // the button in that strip that leaves it. The `active` sentence states what the mode DOES rather
  // than merely naming it: the strip is the only thing on screen once the browser is showing just
  // the document, so it has to answer "why did that key do nothing" on its own.
  'ops.storefront.enter': 'Storefront mode',
  'ops.storefront.active': 'Storefront mode — keys that delete data or change settings are locked',
  'ops.storefront.exit': 'Exit storefront mode',
  'ops.storefront.passcodeTitle': 'Exit storefront mode',
  'ops.storefront.passcodeLabel': 'Venue passcode',
  'ops.storefront.passcodeWrong': 'That is not the passcode. Try again.',

  // MVP-513: the venue's licence countdown, shown every day from seven days out and through the
  // week of grace after the date. Four sentences rather than one with a number in it — "in 1 days",
  // "in 0 days" and "0 days of grace left" are each the wrong thing to print, and the two edge days
  // are exactly the ones that matter. No sentence here offers an action: a venue cannot renew its
  // own licence, so the strip names who to contact and stops.
  'ops.licence.ending': 'This licence expires in {days} days',
  'ops.licence.endingToday': 'This licence expires today — nothing stops',
  'ops.licence.grace': 'This licence expired {days} days ago — {graceDays} days of grace left',
  'ops.licence.graceLastDay': 'This licence expired {days} days ago — today is the last day it works',
  'ops.licence.contact': 'Contact your supplier to renew it',
  'ops.licence.dismissToday': 'Hide until tomorrow',

  // The transport row. `Restart` is deliberately not "Previous": it restarts the current song
  // rather than rewinding the rotation (see `handlePrevious`'s own comment).
  'player.transport.restart': 'Restart',
  'player.transport.play': 'Play',
  'player.transport.pause': 'Pause',
  // MVP-427: the play/pause button laid over the video preview does the same thing as the transport
  // one, so it carried the same name — two visible controls on one screen with one accessible name,
  // which a screen-reader user cannot tell apart and a voice-control user cannot address.
  'player.transport.playPreview': 'Play (video preview)',
  'player.transport.pausePreview': 'Pause (video preview)',
  'player.transport.stop': 'Stop',
  'player.transport.next': 'Next',
  'player.transport.nextNothingQueued': 'Next: nothing queued',

  // MediaSetupCheck.tsx (SPEC AC-WAN-10) — the blocking banner for a media/CDN misconfiguration.
  'player.setup.lead': 'Media playback is not set up correctly.',
  // MVP-290: `engine/preflight.ts` used to hand this component a finished English sentence full of
  // URLs, HTTP statuses and header names (`result.message`) and render it verbatim right after the
  // line above. None of that is actionable by the KJ at the desk, so every failure now shares this
  // one short line instead; the specific technical fact moved to `result.hint`, under `Details`
  // below, exactly where whoever configures the CDN already looks.
  'player.setup.contactAdmin': 'Contact whoever manages this system to fix it.',
  'player.setup.details': 'Details',
  'player.setup.retry': 'Retry check',

  // MVP-307: the non-blocking preflight warning banner in OperatorConsole.tsx. Unlike the five
  // blocking failures above (collapsed to one shared `contactAdmin` line by MVP-290), a warning is
  // dismissable — the KJ is expected to judge whether to carry on — so each of the four
  // `PreflightWarningCode`s gets its own distinct `title` (the bolded lead) and `detail` (the
  // explanatory line). Technical remainder (header names, HTTP statuses) stays out of both and
  // lives in `preflight.ts`'s untranslated `hint` instead, same as the blocking banner's hint.
  'player.setup.warning.noRangeSupport.title': 'Seeking may be slow on this CDN.',
  'player.setup.warning.noRangeSupport.detail': 'The CDN sent the whole file instead of a byte range, so seeking may re-download instead of jumping instantly.',
  'player.setup.warning.rangeNotConfirmed.title': 'Seeking works, but the CDN is not fully confirmed.',
  'player.setup.warning.rangeNotConfirmed.detail': 'The CDN answered, but this page could not fully confirm the response — seeking should still work, just not fully verified.',
  'player.setup.warning.webAudioUnconfirmed.title': 'Audio not checked yet.',
  'player.setup.warning.webAudioUnconfirmed.detail': 'Playback was blocked before your click, so the audio check could not run yet — it will be checked again on the next attempt.',
  'player.setup.warning.webAudioSilent.title': 'This file may be silent.',
  'player.setup.warning.webAudioSilent.detail': 'No sound reached the check within one second — this file may have no audio track or a quiet intro.',

  // LinkQualityBadge.tsx — SPEC §5.1's "a KJ deciding whether tonight will work needs a number".
  'player.link.measuring': 'measuring…',
  'player.link.comfortable': 'comfortable',
  'player.link.conservative': 'conservative',

  // OutputDevicePicker.tsx (SPEC AC-PLAY-12).
  // The trigger names the device it is routing to, not the word "Output" — the one question the
  // button exists to answer is WHICH box the sound is going to, and a bare noun made that a click
  // away. `player.output.trigger` is the fallback for before the device list has come back.
  'player.output.trigger': 'Output',
  'player.output.ariaLabel': 'Audio output: {device}',
  // What to call a device the browser refused to name (no microphone grant, so `label` is empty).
  'player.output.unnamedDevice': 'Output {index}',
  'player.output.popoverTitle': 'AUDIO OUTPUT DEVICE',
  'player.output.systemDefault': 'System default',
  'player.output.unsupported':
    'This browser cannot send audio to a chosen device (needs Chrome 110 or newer). Playback uses the system default device.',
  'player.output.selectLabel': 'Output device',
  'player.output.test': 'Test',
  'player.output.testFailed': 'Could not play the test sound.',
  /** MVP-389: `engine/outputDevice.ts` hands back these two keys instead of the English sentences
   * it used to build — one for a browser with no `setSinkId`, one for a route that failed. */
  'player.output.errorUnsupported': 'This browser cannot choose an output device. Sound goes to the system default.',
  'player.output.errorFailed': 'Could not switch the output device.',

  // BreakMusicPanel.tsx (SPEC AC-Q-10 / AC-PLAY-9). The trigger reuses `player.badge.breakMusic` —
  // the button and the now-playing badge name the same thing, and two keys would let them drift.
  'player.break.popoverTitle': 'BREAK / FILLER MUSIC',
  'player.break.start': 'Start break music',
  'player.break.stop': 'Stop break music',
  'player.break.loadFailed': 'Could not load the break-music list.',
  'player.break.empty':
    'No songs are marked as break music yet. Mark some in the Library — select a song and use “Mark as break music”.',
  'player.break.alsoOnList': 'Also on the list',
  'player.break.upNextShuffled': 'Up next (shuffled)',
  'player.break.duck': 'Duck the audio for an announcement',
  'player.break.startFailed': 'Break music could not be started.',

  // MVP-272 Phase 3c-3 (the shortcut sheet). One sheet, opened from both the cockpit and the
  // library, listing both screens' keys — so its vocabulary is shared and lives together here.
  //
  // Nothing in the KEY column is translated: `formatBindingLabel()` and `LIBRARY_SHORTCUT_ITEMS`'
  // `keys` arrays name physical keycaps ("Space", "Ctrl", "Enter", "Esc", "Page Up", "A–Z"), which
  // read the same on a Thai keyboard as an English one. Translating them would name a key the
  // operator cannot find.
  'ops.shortcuts.title': 'Keyboard shortcuts',
  'ops.shortcuts.sectionCockpit': 'Cockpit',
  'ops.shortcuts.sectionLibrary': 'Library',
  // MVP-339: the sheet is 16 rows in two sections with no filter — finding a shortcut meant reading
  // all of it. The filter matches both what a shortcut DOES and the key itself, so "queue" and
  // "shift" both find the same row.
  'ops.shortcuts.filterLabel': 'Filter shortcuts',
  'ops.shortcuts.filterPlaceholder': 'Type an action or a key',
  'ops.shortcuts.filterClear': 'Clear the filter',
  'ops.shortcuts.noMatch': 'No shortcut matches that.',
  // Settings > Shortcuts, when the operator presses a key another action already owns. `action` is
  // an already-translated `ops.hotkey.*` label.
  'ops.shortcuts.conflict': 'That key is already bound to "{action}". Remap that action first, or choose a different key.',
  // MVP-500 Commit C: the sheet's title while storefront mode is on. Deliberately NOT "eXtreme
  // layout" — the preset moves three keys (next, previous, mute) and leaves the rest of our own
  // table alone, so claiming a layout would be a promise the keymap does not keep.
  'ops.shortcuts.titleStorefront': 'Keyboard shortcuts — storefront mode',

  // The cockpit hotkey table (AC-OPS-5), read on the shortcut sheet and in Settings > Shortcuts.
  // MVP-11 #25: the glossary is Library / Cockpit / Stage — never "audience window".
  'ops.hotkey.playPause': 'Play / pause',
  'ops.hotkey.next': 'Next singer/song',
  // MVP-11 #11: a restart of the current song, NOT a rewind of the rotation.
  'ops.hotkey.previous': 'Restart current song',
  'ops.hotkey.stop': 'Stop',
  'ops.hotkey.keyUp': 'Key up (+1 semitone)',
  'ops.hotkey.keyDown': 'Key down (−1 semitone)',
  'ops.hotkey.keyReset': 'Reset key to 0',
  // MVP-474. Not yet owner-reviewed.
  'ops.hotkey.tempoUp': 'Tempo up (+1%)',
  'ops.hotkey.tempoDown': 'Tempo down (−1%)',
  'ops.hotkey.offsetDown': 'Sync offset −5 ms',
  'ops.hotkey.offsetUp': 'Sync offset +5 ms',
  'ops.hotkey.mute': 'Mute',
  'ops.hotkey.breakMusic': 'Toggle break music',
  'ops.hotkey.fullscreen': 'Fullscreen the Stage',
  'ops.hotkey.toggleLyrics': 'Toggle lyrics',
  'ops.hotkey.search': 'Focus search',
  'ops.hotkey.shortcutSheet': 'Show this shortcut sheet',
  'ops.hotkey.undo': 'Undo',
  // MVP-547: names the ROW it lands on, not just the action — "Accept" alone would leave a KJ with
  // four people waiting guessing which of the four the key takes.
  'ops.hotkey.acceptTopRequest': 'Accept the oldest pending request',
  'ops.hotkey.exitStorefront': 'Leave storefront mode',

  // MVP-500 Commit C: labels that REPLACE the three above while storefront mode is on. These are
  // the keys the preset does NOT move but a KJ arriving from eXtreme expects to do something else.
  // Space is the worst of them — expecting a song title and stopping the song in front of a room —
  // so the sheet says what it does here before anyone presses it.
  'ops.hotkey.storefront.playPause': 'Play / Pause (eXtreme uses this key to show the song title)',
  'ops.hotkey.storefront.tempoUp': 'Tempo up (+1%) (eXtreme uses this key to move through the queue)',
  'ops.hotkey.storefront.tempoDown': 'Tempo down (−1%) (eXtreme uses this key to move through the queue)',

  // The library half of the same sheet (AC-UI-5). MVP-263: "focused" is the single roving item,
  // "selection" is the checkbox set — never one word for both, in either language.
  'library.shortcut.focusSearch': 'Focus search',
  'library.shortcut.move': 'Move the focus',
  'library.shortcut.first': 'Jump to the first song',
  'library.shortcut.last': 'Jump to the last song',
  'library.shortcut.pageUp': 'Move up a page',
  'library.shortcut.pageDown': 'Move down a page',
  'library.shortcut.play': 'Play the focused song now',
  'library.shortcut.queue': 'Add the selection to the queue',
  'library.shortcut.queueNext': 'Add the selection to the queue, play next',
  'library.shortcut.escape': 'Close the panel, or clear the selection',
  'library.shortcut.sheet': 'Show this shortcut sheet',
  'library.shortcut.typeAhead': 'Type-ahead: jump to a title starting with that letter',

  // MVP-272 Phase 3c-4 (lyrics operator chrome). `LyricStage` is the one component that mounts on
  // BOTH the audience Stage and the cockpit: everything below is behind its `!audienceMode` guard,
  // so it is read by an operator leaning into a monitor, never by the room (Phase 2 owns that half,
  // under `stage.`). File-format names (LRC, ASS, WebVTT, CD+G) and encoding identifiers (UTF-8,
  // Shift-JIS) are not translated — they name a standard, and a KJ matching one against a filename
  // needs the same letters the file has.
  //
  // The first plural in this dictionary. English needs two forms and Thai needs one, so the caller
  // picks the key rather than the dictionary trying to hold both — a `{count}`-only phrasing (the
  // trick `requests.pendingCount` uses) does not fit a sentence this one
  // has to start.
  'lyrics.warningCountOne': '1 warning',
  'lyrics.warningCountMany': '{count} warnings',

  'lyrics.settings': 'Lyric settings',
  'lyrics.fixThisFile': 'Fix this file',
  'lyrics.addLyrics': 'Add lyrics',
  // The operator-only notice when jassub fails at runtime and the generic wipe takes over.
  // MVP-297: `{reason}` used to be an English sentence handed over by `assEnvironment.ts`, so the
  // Thai read as a Thai sentence with an English one inside its brackets. The four causes are a
  // closed set, so each is its own key and the caller passes `t(key)` — half-translated is worse
  // than all-English, because it looks finished.
  'lyrics.assFallback': 'Advanced subtitle rendering unavailable ({reason}) — showing the simplified lyric wipe instead.',
  'lyrics.assUnsupported.worker': 'this browser has no Web Worker support',
  'lyrics.assUnsupported.webAssembly': 'this browser has no WebAssembly support',
  'lyrics.assUnsupported.offscreenCanvas': 'this browser has no OffscreenCanvas support',
  'lyrics.assUnsupported.loadFailed': 'the subtitle renderer could not be loaded',

  // MVP-331: `cdg.ts` returns a code for each of these; `CdgCanvas.tsx` is the only thing that turns
  // one into a sentence. `generic` covers an errored canvas with no code — unreachable through
  // `fetchCdgDecoder` itself, but a blank error box would be worse than a general one.
  'lyrics.cdg.error.http': 'Could not load the CD+G file (HTTP {status}).',
  'lyrics.cdg.error.unreachable': 'Could not reach the server for this CD+G file.',
  'lyrics.cdg.error.empty': 'This CD+G file is empty.',
  'lyrics.cdg.error.generic': 'Could not load the CD+G file.',
  'lyrics.loadFailed': "Couldn't load lyrics: {reason}",
  // `describeLoadError`'s three known cases. Anything else falls through as the raw error code,
  // which is technical output, not copy.
  'lyrics.loadError.missingFile': 'no sidecar file is attached to this track.',
  'lyrics.loadError.http': 'the CDN returned {status}.',
  'lyrics.loadError.emptyFile': 'the file is empty.',

  'lyrics.source.label': 'Lyric source',
  'lyrics.source.edit': 'Edit',

  'lyrics.offset.label': 'Lyric sync offset',
  'lyrics.offset.saveFailed': 'Could not save the offset — it will only apply for this session.',

  // Only the language hint in brackets is copy; the encoding's own name is the identifier the file
  // was actually saved with.
  'lyrics.encoding.label': 'Lyric file text encoding',
  'lyrics.encoding.japanese': 'Japanese',
  'lyrics.encoding.chinese': 'Chinese',
  'lyrics.encoding.latin': 'Latin',
  'lyrics.encoding.thai': 'Thai',

  // TapToTimeBar. Play/pause reuse `player.transport.*` — the same two words for the same two
  // states everywhere in the app.
  'lyrics.tap.back': 'Back 2 seconds',
  'lyrics.tap.forward': 'Forward 2 seconds',
  'lyrics.tap.tap': 'Tap',

  // LyricEditor (AC-LYR-9's tap-to-time editor).
  'lyrics.editor.title': 'Edit lyrics — {title}',
  'lyrics.editor.assShiftHint': 'Shift the whole file — keeps every style/position tag byte-for-byte',
  'lyrics.editor.wholeFileShift': 'Whole-file shift',
  'lyrics.editor.applyAndSave': 'Apply & save',
  'lyrics.editor.textLabel': 'Lyrics — one line per row',
  'lyrics.editor.textPlaceholder': 'First line of the song\nSecond line\n…',
  'lyrics.editor.startTiming': 'Start timing',
  'lyrics.editor.import': 'Import a file (.lrc, .ass, .ssa, .srt, .vtt)',
  'lyrics.editor.needOneLine': 'Type at least one line first, one line per lyric.',
  'lyrics.editor.importUnsupported': 'Can\'t import "{file}" — supported types are .lrc, .ass, .ssa, .srt and .vtt.',
  'lyrics.editor.importNoLines': '"{file}" parsed but has no timed lines — check the file\'s contents.',
  'lyrics.editor.importUnreadable': 'Could not read "{file}".',
  'lyrics.editor.saveFailed': 'Could not save the lyrics — check your connection and try again.',
  'lyrics.editor.shiftSaveFailed': 'Could not save the shifted file.',
  'lyrics.editor.warningsOne': '1 warning from the source file — timings below are still usable.',
  'lyrics.editor.warningsMany': '{count} warnings from the source file — timings below are still usable.',
  // The tap cursor's readout, the one thing on this screen that changes with every tap.
  'lyrics.editor.statusWord': 'Word {index} of {total} — "{word}"',
  'lyrics.editor.statusAllStamped': 'All lines stamped — nudge anything that is off, then save.',
  'lyrics.editor.statusLine': 'Line {index} of {total}',
  'lyrics.editor.jumpCursor': 'Jump the tap cursor to this line',
  'lyrics.editor.nudgeLineEarlier': 'Nudge this line {step}ms earlier',
  'lyrics.editor.nudgeLineLater': 'Nudge this line {step}ms later',
  'lyrics.editor.split': 'Split',
  'lyrics.editor.merge': 'Merge ↓',
  'lyrics.editor.wordByWord': 'Word-by-word',
  'lyrics.editor.shiftWholeFile': 'Shift the whole file',
  'lyrics.editor.shiftAllEarlier': 'Shift every line {step}ms earlier',
  'lyrics.editor.shiftAllLater': 'Shift every line {step}ms later',
  'lyrics.editor.languageLabel': 'Language (optional)',
  'lyrics.editor.languagePlaceholder': 'en, th, ja…',
  'lyrics.editor.saveFormat': 'Save as format',
  'lyrics.editor.formatEnhanced': 'Enhanced LRC (word-level)',
  'lyrics.editor.formatAss': 'ASS (karaoke styling)',
  'lyrics.editor.save': 'Save lyrics',

  // MVP-272 Phase 4a (the library's result surface: one tile, one row, the download affordance and
  // the count above them). Counts keep going through `toLocaleString()` in `resultSummary.ts` —
  // DESIGN.md §7's "104,912 songs" grouping rule stays in exactly one place, and the already
  // grouped string arrives here as a `{count}` param.
  'library.kind.video': 'Video',
  'library.kind.karaoke': 'Karaoke',
  // The format's own name, like the lyric formats — not translated in either dictionary.
  'library.kind.cdg': 'CD+G',
  'library.kind.audio': 'Audio',

  'library.song.label': '{title} by {artist}',
  'library.song.select': 'Select {title}',
  'library.song.catalogueId': 'Catalogue id',
  // Short on purpose: it sits in a row of badges beside the duration, where
  // `player.badge.breakMusic`'s two words do not fit.
  'library.song.breakBadge': 'Break',
  'library.song.breakTitle': 'Plays on the break-music bus',
  'library.song.lyricsAvailable': 'Lyrics available',
  // MVP-656: the `mic-off` flag on a card and a row. Says what the operator can DO with the song —
  // the fact underneath it (the guide vocal sits on its own channel) is the scanner's business, and
  // "guideVocal: right" on a poster would mean nothing to a KJ mid-shift.
  'library.song.vocalOff': 'Guide vocal can be switched off',

  'library.download.action': 'Download',
  'library.download.busy': 'Downloading…',
  'library.download.label': 'Download {title}',

  // MVP-459. "Audition", not "Play": what this starts is the short preview clip, and the operator
  // roles already have a Play that means the whole song on the Stage. Downloader-only, so the two
  // words are never on one screen together — but the same person may hold both accounts.
  'library.audition.action': 'Listen to a preview',
  'library.audition.label': 'Listen to a preview of {title}',
  'library.audition.stop': 'Stop the preview',
  'library.audition.stopLabel': 'Stop the preview of {title}',

  // MVP-460. What the pick DOES, never what is on the channel: a karaoke master usually carries the
  // guide vocal on one side, but which side is a fact about the file that nothing here knows —
  // naming it "vocal" would be a guess stated as a label. See `vocalRouting.ts`'s header, which
  // settled the same question for the Cockpit's version of this control.
  'library.audition.channelLeft': 'Hear the left channel only',
  'library.audition.channelRight': 'Hear the right channel only',
  'library.audition.channelBoth': 'Back to both channels',

  // ResultSummary (AC-UI-6 / AC-UI-9). English needs a singular and a plural of each; Thai needs
  // one — same `…One`/`…Many` split the lyric warnings introduced.
  'library.results.searchOne': '{count} result for “{query}”',
  'library.results.searchMany': '{count} results for “{query}”',
  'library.results.filtered': '{visible} of {total} songs',
  'library.results.totalOne': '{count} song',
  'library.results.totalMany': '{count} songs',
  // AC-UI-9 quotes this sentence verbatim ("N of your selection are no longer visible"), so
  // resultSummary.test.ts asserts the English against this dictionary rather than against a literal
  // in the module that no longer holds one.
  'library.selection.hiddenOne': '{count} of your selection is no longer visible',
  'library.selection.hiddenMany': '{count} of your selection are no longer visible',
  'library.selection.count': '{count} selected',
  'library.selection.clear': 'Clear',

  // MVP-272 Phase 4b (everything that narrows the grid: the facet rail, the chips above it, the
  // sort control and the density toggle).
  //
  // The facet VALUES below are only the ones this app writes. A genre, a decade and a language
  // display name arrive from the catalogue itself and pass through untranslated — `Intl.DisplayNames`
  // already localises the language names to the browser's locale, and inventing a Thai word for a
  // genre string nobody has entered yet would be inventing data, not copy.
  'library.facet.kind': 'Type',
  'library.facet.genre': 'Genre',
  'library.facet.language': 'Language',
  'library.facet.decade': 'Decade',
  'library.facet.lyrics': 'Lyrics',
  'library.facet.duration': 'Duration',
  'library.facet.resolution': 'Resolution',
  'library.facet.played': 'Play history',
  // MVP-346: the rail half of MVP-249's break-music flag. The group heading names the thing being
  // filtered on, not the question — same shape as 'Play history' above.
  'library.facet.breakMusic': 'Break music',
  // MVP-658: the rail row for the guide-vocal channel. Its VALUES reuse the Cockpit editor's own
  // words (`player.vocal.tag*`) rather than a second vocabulary for the same fact.
  'library.facet.guideVocal': 'Guide vocal',

  // The four kind labels are NOT repeated here — the TYPE facet reads `library.kind.*` above, the
  // same entries the tile badge reads, so the rail and the tile can never call one song two names.
  'library.facet.duration.under2': 'Under 2 min',
  'library.facet.duration.2to4': '2–4 min',
  'library.facet.duration.4to6': '4–6 min',
  'library.facet.duration.over6': '6+ min',
  'library.facet.duration.unknown': 'Unknown length',
  'library.facet.resolution.unknown': 'Unknown resolution',
  'library.facet.played.played': 'Played before',
  'library.facet.played.never': 'Never played',
  'library.facet.breakMusic.break': 'Break music only',
  'library.facet.breakMusic.regular': 'Regular songs only',
  'library.facet.lyrics.wordLevel': 'Word-level lyrics',
  'library.facet.lyrics.line': 'Line-level lyrics',
  'library.facet.lyrics.none': 'No lyrics',
  // MVP-311: the genre buckets, authored on the artist and inherited by every one of that
  // artist's songs (src/utils/artist-genre.js's ARTIST_GENRES is the slug source of truth this
  // dictionary's keys mirror). English labels, not transliterations — "Luk thung"/"Mor lam" are the
  // names an English speaker would already recognise these genres by.
  'library.facet.genre.lukThung': 'Luk thung',
  'library.facet.genre.morLam': 'Mor lam',
  'library.facet.genre.string': 'String',
  'library.facet.genre.sakon': 'International',
  'library.facet.genre.chinese': 'Chinese',
  'library.facet.genre.phueaChiwit': 'Songs for life',
  // MVP-321: the seventh bucket — Southern Thai dialect (เพลงใต้ / เพลงปักษ์ใต้), not a new musical
  // form like the other six. MVP-316's genre research found 19 artists (~281 songs) provisionally
  // parked under `luk-thung` for lack of anywhere else to go.
  'library.facet.genre.southern': 'Southern',
  // MVP-321: the server's sentinel for "this song's artist has no genre set at all" —
  // `COALESCE(NULLIF(genre, ''), 'unknown')` in strapi-template/src/api/catalog/services/facets.js.
  // `unknown` is never a value `ARTIST_GENRES` can hold; it is what the facet query reports in that
  // column's absence. Gets its own key (like `duration.unknown`/`decade.unknown`/
  // `resolution.unknown` below) rather than reusing the shared `library.facet.unspecified` string,
  // so the genre rail no longer falls back to rendering the raw `unknown` slug.
  'library.facet.genre.unknown': 'Unspecified',
  // Shared by the language group: the song has no value on file, which is a different thing from a
  // value called "unknown". (Genre stopped sharing this one in MVP-321 — see
  // `library.facet.genre.unknown` above.)
  'library.facet.unspecified': 'Unspecified',
  'library.facet.decade.unknown': 'Unknown year',

  'library.filters.title': 'Filters',
  'library.filters.clearAll': 'Clear all',
  'library.filters.innerSearchLabel': 'Filter {group} values',
  'library.filters.innerSearchPlaceholder': 'Filter…',
  'library.filters.selectShown': 'Select shown',
  'library.filters.clearShown': 'Clear shown',
  'library.filters.noMatches': 'No matches',

  'library.activeFilters.label': 'Active filters',
  'library.activeFilters.remove': 'Remove filter {group}: {value}',

  'library.sort.label': 'Sort songs by',
  'library.sort.relevanceTitle': 'Sorted by best match while searching',
  'library.sort.relevanceReason': 'Sorted by best match',
  'library.sort.codeDesc': 'Code (high–low)',
  'library.sort.code': 'Code (low–high)',
  'library.sort.title': 'Title (A–Z)',
  'library.sort.artist': 'Artist (A–Z)',
  'library.sort.added': 'Recently added',
  'library.sort.played': 'Most played',
  'library.sort.duration': 'Duration',

  'library.density.label': 'Library view density',
  'library.density.grid': 'Grid view',
  'library.density.list': 'List view',
  'library.density.compact': 'Compact list view',

  // MVP-432: card size in the grid view. Separate from density, which picks the row's shape.
  'library.zoom.label': 'Card size',
  'library.zoom.out': 'Smaller cards',
  'library.zoom.in': 'Bigger cards',
  'library.zoom.sm': 'Small',
  'library.zoom.md': 'Medium',
  'library.zoom.lg': 'Large',
  'library.zoom.xl': 'Largest',
  'library.zoom.reset': 'Reset',
  // MVP-448, DESIGN §6.2: a disabled control states why. Each replaces the button's own label while
  // it is unavailable — as the tooltip AND as its accessible description — so "Bigger cards" does
  // not sit under a pointer on a button that will not make anything bigger. `atLargest` names the
  // width, not the enum: past the column floor the widest step draws the same grid as the one below
  // it, and "that is as big as this window fits" is the true reason (MVP-447).
  'library.zoom.atSmallest': 'Already the smallest card size',
  'library.zoom.atLargest': 'As big as these cards get in this window — widen it or close a panel for more',
  'library.zoom.atDefault': 'Already at the default card size',

  // MVP-442: the floating back-to-top button over the results.
  'library.backToTop': 'Back to the first song',

  // MVP-272 Phase 4c (`LibraryScreen.tsx` itself: the toolbar, the action bar, every empty and
  // error state, the singer picker and the toasts).
  'common.serverUnreachable': 'Could not reach the server.',

  'library.search.label': 'Search library',
  'library.search.placeholder': 'Search title, artist…',
  'library.filters.show': 'Show filters',
  'library.filters.hide': 'Hide filters',
  // MVP-304. Both of these say the same thing from two places: the rail is short because of what
  // this catalogue knows about itself, not because the app ran out of filters. Naming the count
  // makes it a measurable gap somebody can go and close, rather than a vague absence.
  // MVP-312: "year" and "language" dropped from both strings — those two are no longer facets this
  // rail will ever offer (measured: every production file's metadata was stripped by an upstream
  // ffmpeg remux, so there is nothing "carrying" data would mean), not facets waiting on data entry
  // the way genre and lyrics are.
  'library.filters.none': 'No filters yet — this catalogue has no genre or lyrics on file',
  'library.filters.moreWhenData': '{count} more filters appear once songs carry genre or lyrics.',
  // SongInspector (MVP-415) — DESIGN §10's right-hand panel, read-only.
  'library.inspector.title': 'Song details',
  'library.inspector.show': 'Show song details',
  'library.inspector.hide': 'Hide song details',
  'library.inspector.emptyTitle': 'Pick a song to see its details',
  'library.inspector.featuring': 'feat. {name}',
  'library.inspector.hasLyrics': 'Lyrics',
  'library.inspector.breakMusic': 'Break music',
  'library.inspector.status': 'Status: {status}',
  'library.inspector.code': 'Code',
  'library.inspector.duration': 'Length',
  'library.inspector.language': 'Language',
  'library.inspector.genre': 'Genre',
  'library.inspector.year': 'Year',
  'library.inspector.resolution': 'Video',
  'library.inspector.playCount': 'Played',
  'library.inspector.lastPlayed': 'Last played',
  'library.inspector.never': 'Never',
  'library.inspector.added': 'Added',
  'library.export.action': 'Export XLSX',
  'library.export.busy': 'Exporting…',
  // The .xlsx COLUMN HEADINGS are deliberately not here. `exportXlsx.ts` reproduces the legacy
  // karaoke site's own sheet — Code / Name / Artist / Company / File Name — which is a file
  // interchange format other tools read, not copy on a screen.
  'library.export.title': 'Download every song matching the current search and filters as an .xlsx file',
  'library.export.readyTitle': 'Export ready',
  'library.export.readyDetail': '{count} songs written to an .xlsx file.',
  'library.export.failedTitle': 'Export failed',

  // The action bar. Every one of these counts off the SAME target set the click will use
  // (`resolveActionTargets`), which is why the plural branches are separate entries rather than a
  // noun spliced into a sentence — Thai builds the whole sentence differently.
  'library.action.selectFirst': 'Select a song first',
  'library.action.playNow': 'Play now (no singer)',
  'library.action.playNowTitle': 'Play “{title}” now without adding it to the rotation',
  'library.action.playNowOneOnly':
    'Play now takes one song, and {count} are selected. Clear the selection, or use “Play next”.',

  'library.action.queue': 'Add to queue',
  'library.action.queueCount': 'Add {count} to queue',
  'library.action.queueOneTitle': 'Add “{title}” to the queue',
  'library.action.queueManyTitle': 'Add all {count} selected songs to the queue, for one singer',
  'library.action.next': 'Play next',
  'library.action.nextCount': 'Play {count} next',
  'library.action.nextOneTitle': 'Play “{title}” next',
  'library.action.nextManyTitle': 'Play all {count} selected songs next, for one singer',

  'library.action.breakMark': 'Mark as break music',
  'library.action.breakUnmark': 'Unmark break music',
  'library.action.breakMarkOneTitle': 'Play this song on the break-music bus between singers',
  'library.action.breakMarkManyTitle': 'Play {count} songs on the break-music bus between singers',
  'library.action.breakUnmarkOneTitle': 'Stop the break-music bus from playing this song',
  'library.action.breakUnmarkManyTitle': 'Stop the break-music bus from playing {count} songs',
  'library.action.breakMarkedTitle': 'Marked as break music',
  'library.action.breakUnmarkedTitle': 'Removed from break music',
  'library.action.breakUpdatedOne': '{count} song updated.',
  'library.action.breakUpdatedMany': '{count} songs updated.',
  'library.action.breakFailed': 'Could not update break music',

  // MVP-354. The button says one word and the confirmation carries the count — see
  // `describeDeleteAction`'s own comment for why the label never grows a number.
  'library.action.delete': 'Delete',
  'library.action.deleteOneTitle': 'Delete “{title}” from the catalogue',
  'library.action.deleteManyTitle': 'Delete {count} songs from the catalogue',
  'library.action.deletedTitle': 'Deleted from the catalogue',
  'library.action.deletedOne': '“{title}” is no longer in the catalogue.',
  'library.action.deletedMany': '{count} songs are no longer in the catalogue.',
  'library.action.deleteFailed': 'Could not delete',
  'library.action.restoreFailed': 'Could not undo',
  'library.action.restoreFailedDetail': '{count} songs are still deleted. Try again from the admin panel.',
  // DESIGN.md §7: a destructive confirmation names the object and the count. The description is
  // the reason an admin can press this without a knot in their stomach — say what survives, not
  // just what goes.
  'library.delete.confirmOne': 'Delete “{title}” from the catalogue?',
  'library.delete.confirmMany': 'Delete {count} songs from the catalogue?',
  'library.delete.description':
    'The songs disappear from search, the library and the break-music list. The files themselves are kept, and you can undo this straight after.',
  'library.delete.confirmAction': 'Delete',

  'library.corrected': 'Showing results for “{to}” instead of “{from}”.',
  'library.results.label': 'Songs',
  'library.loadingMore': 'Loading more songs…',

  'library.error.title': "Couldn't load the library",
  'library.empty.noMatchTitle': 'No matches',
  'library.empty.noMatchDetail': 'Try a different search or clear your filters.',
  'library.empty.clearFilters': 'Clear filters',
  // MVP-11 #2: an empty catalogue is not a developer setup screen. `mv-ingest` is the CLI's own
  // name and stays as written in both dictionaries.
  'library.empty.noSongsTitle': 'No songs yet',
  'library.empty.noSongsDetail': 'Ingest your library with the mv-ingest CLI, then refresh this screen.',
  'library.empty.refresh': 'Refresh',

  // The singer picker, and the queue result it leads to.
  'library.singer.title': 'Who is singing?',
  'library.singer.queueTitleOne': 'Queue: “{title}”',
  'library.singer.queueTitleMany': 'Queue: {count} songs',
  'library.singer.nextTitleOne': 'Play next: “{title}”',
  'library.singer.nextTitleMany': 'Play next: {count} songs',
  'library.singer.useAgain': 'Use “{name}” again',
  'library.singer.nameLabel': 'Singer name',
  'library.singer.namePlaceholder': 'Type a name…',
  'library.singer.emptyHint': 'Type a name or pick one below',
  // MVP-330: `useSingerDirectory`'s own fetch, not the caller's `singers` prop — shown while it is
  // still in flight (no retry needed, it will resolve on its own) or once it has failed (paired
  // with `common.retry`, wired to the same `refresh()` this hook already exposed for exactly this).
  'library.singer.directoryLoading': 'Loading the singer list…',
  // MVP-416 (AC-Q-11). A statement, not a warning: the KJ decides whether the repeat is wanted.
  // MVP-420: was "Already sung tonight" / "{name} sang {song}". The rows behind this warning can be
  // days old (see the `history.*` note above), so the claim moved to the session and each row now
  // carries WHEN — without it a KJ cannot tell a repeat from twenty minutes ago apart from one from
  // last Tuesday, and the two call for opposite decisions.
  'library.singer.sungTonight': 'Already sung this session',
  'library.singer.sungTonightRow': '{name} sang {song} · {when}',
  'library.singer.directoryError': "Couldn't load the singer list, so adding a new singer is on hold",
  // Four entries rather than two plus a " — play next" suffix: a suffix bolted onto a finished
  // button label is an English sentence shape, and Thai does not build the phrase that way.
  'library.singer.useExisting': 'Use {name}',
  'library.singer.useExistingNext': 'Use {name} — play next',
  'library.singer.addNew': 'Add new singer',
  'library.singer.addNewNext': 'Add new singer — play next',
  'library.queued.one': 'Queued “{title}”',
  'library.queued.many': 'Queued {count} songs',
  'library.queued.for': 'For {name}.',
  'library.queued.forNext': 'For {name}, up next.',
  'library.queued.failed': 'Could not add to the queue.',
  // MVP-263: a partial failure has to say how far it got — "could not add to the queue" after nine
  // of fourteen already landed sends the KJ looking for a queue that is not empty.
  'library.queued.partial': 'Added {done} of {total}, then stopped. {reason}',

  // MVP-272 Phase 5 (Settings). Five sections, 55 controls, and the one control on this screen that
  // is not translated at all: the language picker's own options are ENDONYMS ("ไทย"/"English"). A
  // KJ who mis-set this needs to recognise the name of their own language to get back out, which is
  // exactly what translating it would take away.
  'settings.loadErrorTitle': "Couldn't load settings",

  // MVP-396: the tablist's accessible name — what the five tabs are tabs OF. The tab labels
  // themselves reuse the section headings below, so a tab never disagrees with the heading it opens.
  'settings.tabsLabel': 'Settings sections',
  'settings.tonight': 'Requests',
  // MVP-274 R2: says out loud what separates this section from the rest of the screen, and why the
  // same four controls are also on the Cockpit now.
  'settings.tonight.note': 'These four change every shift. They are on the Cockpit too, next to the join code.',
  'settings.session.loadErrorTitle': "Couldn't load tonight's session",
  'settings.session.none': 'No session is active — start one from the cockpit.',
  'settings.session.requestMode': 'Request mode',
  'settings.session.requestModeOpen': 'Open: requests join the queue',
  'settings.session.requestModeReview': 'Review: you approve each one',
  'settings.session.requestModeClosed': 'Closed: no requests tonight',
  'settings.session.songsPerTurn': 'Songs per turn',
  'settings.session.turnaround': 'Turnaround',
  'settings.session.passcode': 'Venue passcode (empty = off)',
  'settings.session.passcodeRandom': 'Random',
  'settings.session.message': 'Message to guests',
  // MVP-337: unknown (non-ApiError) failure saving a nightly setting — an ApiError shows its own
  // message instead; this is the fallback for everything else so the UI never renders `[object
  // Object]` or an `Error:`-prefixed dump of a caught value.
  'settings.session.saveError': "Couldn't save that setting. Try again.",

  // MVP-472 follow-up 2. The room picker. Worded as a fact about THIS SCREEN, not about the shop:
  // every other control here changes something for everybody, and this one changes only the machine
  // the KJ is standing at. Hidden entirely on a shop with one room — see RoomSettings.tsx.
  'settings.room': 'This screen runs',
  'settings.roomDefault': 'The usual room',
  'settings.room.note': 'Saved on this computer only. Another screen in another room keeps its own.',
  'settings.room.confirmTitle': 'Switch this screen to {room}?',
  'settings.room.confirmBody': 'The page reloads, and anything playing on it stops. The queue, the requests and the Stage all follow the new room.',
  'settings.room.confirmAction': 'Switch and reload',
  // MVP-472 follow-up 1. The two values the owner decided a room may have of its own. "Use the
  // shop's" rather than "None" on purpose: the screen still has a theme either way, and an
  // operator reading the row has to be able to tell that nothing is switched off.
  'settings.room.stageTheme': "This room's stage theme",
  'settings.room.marquee': "This room's ticker",
  'settings.room.useVenue': "Use the shop's",
  'settings.room.overrideNote': "Only this room. Left blank, it follows the shop's own stage settings.",

  'settings.appearance': 'Appearance',
  'settings.language': 'Language',
  'settings.theme': 'Theme',
  'settings.themeDark': 'Dark',
  'settings.themeLight': 'Light',
  // MVP-502. Not yet owner-reviewed.
  'settings.palette': 'Palette',
  'settings.paletteHouse': 'House (default)',
  'settings.paletteAstryx': 'Astryx',
  'settings.stageTheme': 'Stage theme',
  // The five Stage palettes are named after their accent colour. Kept as written in both
  // dictionaries: they are the swatch names a KJ matches against what is on the Stage, and there is
  // no Thai word that identifies "the amber one" more clearly than the English does.
  'settings.stageThemeAmber': 'Amber',
  'settings.stageThemeCyan': 'Cyan',
  'settings.stageThemeLime': 'Lime',
  'settings.stageThemeRose': 'Rose',
  'settings.stageThemeHighContrast': 'High contrast',

  'settings.stage': 'Stage',
  'settings.resetDefaults': 'Reset to defaults',
  // MVP-324: Reset used to fire straight from the button. `section` is the group's own translated
  // name (Ticker/Attract/Overlays) rather than a raw section key, so the confirm reads as a
  // sentence in either language rather than a label glued onto a template.
  'settings.resetConfirmTitle': 'Reset {section} to defaults?',
  'settings.resetConfirmDetail': 'Every {section} setting on this screen will be replaced with its default value.',

  'settings.ticker': 'Ticker',
  'settings.ticker.enabled': 'Show the ticker',
  'settings.ticker.text': 'Ticker text',
  'settings.ticker.speed': 'Ticker speed',
  'settings.ticker.position': 'Ticker position',
  'settings.ticker.positionTop': 'Top',
  'settings.ticker.positionBottom': 'Bottom',

  'settings.attract': 'Attract',
  'settings.attract.mode': 'Attract mode',
  'settings.attract.modeStill': 'Still',
  'settings.attract.modeSlideshow': 'Slideshow',
  'settings.attract.interval': 'Slide interval',
  'settings.attract.showJoinQr': 'Show the join QR',
  'settings.attract.showRotation': 'Show the rotation',
  'settings.attract.message': 'Attract message',

  'settings.overlays': 'Overlays',
  'settings.overlays.nowPlaying': 'Now playing',
  'settings.overlays.upNext': 'Up next',
  'settings.overlays.countdown': 'Countdown',
  'settings.overlays.missingLyrics': 'Missing-lyrics card',
  'settings.overlays.missingLyricsNote':
    'Dims the Stage when a song has no lyric file. Leave off if your videos have lyrics burned in.',
  'settings.overlays.duration': 'Overlay duration',

  // MVP-270's "never a disabled control with no stated reason" sentences, named by
  // `settingsDependents.ts` and resolved here.
  'settings.dependent.ticker': 'Turn on the ticker to edit this.',
  'settings.dependent.slideInterval': 'Switch Attract mode to Slideshow to edit this.',
  'settings.dependent.overlayDuration': 'Turn on Now playing or Up next to edit this.',

  'settings.shortcuts': 'Shortcuts',
  // MVP-372: names the way out. The capture is modal in effect, and an exit nobody is told about
  // is not an exit.
  'settings.shortcuts.pressKey': 'Press a key… Esc or Tab to leave it as it is.',
  'settings.shortcuts.cancel': 'Cancel',
  'settings.shortcuts.change': 'Change',

  'settings.backup': 'Backup',
  'settings.payment': 'Payments',
  'settings.payment.note': 'The account shops pay into for their plan. With a PromptPay ID they see a QR first; otherwise the bank account.',
  'settings.payment.promptPayId': 'PromptPay (mobile number or tax ID)',
  'settings.payment.save': 'Save',
  'settings.payment.saved': 'Saved. Shops now see this account on the plans page.',
  'settings.payment.failed': 'Could not save the payment account.',
  'settings.backup.export': 'Export settings',
  'settings.backup.import': 'Import settings',
  // `settingsCodec.ts` names one of these when an import is refused; it is a pure module with no
  // provider, so it hands back the key and this screen resolves it.
  'settings.import.notJson': 'That file is not valid JSON.',
  'settings.import.notOurs': 'That file is not an RMS Karaoke settings export.',
  'settings.import.empty': 'That file has no settings in it.',
  // MVP-404: not the codec's, unlike the three above — this one is the READ failing, before any
  // parsing happens. A file picked and then moved, renamed, unplugged or revoked between the
  // picker closing and `file.text()` resolving lands here. It names no cause because the browser
  // does not give one worth repeating, and the one recovery is the same either way.
  'settings.import.unreadable': "That file couldn't be read. Try picking it again.",
  // MVP-324: Import used to overwrite the live config on the same click that picked the file.
  // `filename` is the exact file being read (this screen already has it before the dialog opens),
  // and the two detail keys mirror the singular/plural split `admin.ingest.*` already uses rather
  // than gluing an English plural `s` onto a template Thai has no matching form for.
  'settings.importConfirmTitle': 'Import settings from {filename}?',
  'settings.importConfirmDetailOne': 'This will overwrite 1 field with the value from that file.',
  'settings.importConfirmDetailMany': 'This will overwrite {count} fields with the values from that file.',
  // MVP-326: Settings autosaves with no Save button (this file's own header comment), so a
  // successful export or import has no other receipt on screen — naming the actual file is the
  // one detail that ties the toast to the button the operator just pressed rather than to nothing.
  'settings.toastExported': 'Exported settings to {filename}',
  'settings.toastImported': 'Imported settings from {filename}',

  // Unit suffixes on the number steppers. Short by design — they sit inside the control, beside the
  // number, not as a sentence.
  'settings.unit.sec': 'sec',
  'settings.unit.pxPerSec': 'px/s',

  // MVP-272 Phase 6 (the three admin screens: Activity, Accounts, Ingest). Read by one or two
  // people who run the venue rather than by a KJ mid-shift, but they are still the same people —
  // this is the app's own copy, not a developer console.

  // The audit log (MVP-227). `audit.ts` composes these; see its own header for why that module
  // takes a `t` instead of handing back keys.
  'admin.activity.title': 'Activity',
  'admin.activity.refresh': 'Refresh',
  'admin.activity.loadMore': 'Load more',
  'admin.activity.emptyTitle': 'Nothing yet',
  'admin.activity.emptyDetail': 'Invites, account changes and sign-ins show up here.',
  'admin.activity.loadError': 'Could not load the activity log.',
  // MVP-444: the log pages as you scroll now. `loadMore` above is kept — it is the label on the
  // Retry path's button, which is the one place a person still presses to get a page.
  'admin.activity.loadingMore': 'Loading more activity…',
  'admin.activity.moreError': 'Could not load the next page.',
  'admin.activity.filters': 'Filter',
  'admin.activity.filterAction': 'What happened',
  'admin.activity.filterActionAny': 'Anything',
  'admin.activity.filterActor': 'Who did it',
  'admin.activity.filterActorAny': 'Anyone',
  'admin.activity.filterCount': '{count} matching entries',
  'admin.activity.filterClear': 'Clear filters',
  'admin.activity.filterEmptyTitle': 'Nothing matches this filter',
  'admin.activity.filterEmptyDetail': 'Try a different action or person, or clear the filters to see everything.',
  // MVP-444: a short name per action for the filter's dropdown. The log's own rows are whole
  // sentences ("Issued an invite link for KJ"), and a dropdown of sentences is unreadable — these
  // are the same events named rather than narrated. An action this build has never heard of is not
  // in the list at all; the server sends its own vocabulary, and anything missing here falls back
  // to the raw string, the same way `auditSentence` does.
  'admin.audit.action.inviteCreate': 'Invite issued',
  'admin.audit.action.inviteRevoke': 'Invite revoked',
  'admin.audit.action.inviteRedeem': 'Invite used',
  'admin.audit.action.accountUpdate': 'Account changed',
  'admin.audit.action.loginSuccess': 'Signed in',
  'admin.audit.action.loginLocked': 'Locked out',
  'admin.audit.action.loginRefused': 'Sign-in refused',
  'admin.audit.action.sessionJoinCodeRotated': 'Join code changed',
  'admin.audit.action.passwordChange': 'Password changed',
  'admin.audit.action.songDownload': 'Song file downloaded',
  'admin.audit.action.songDelete': 'Song deleted',
  'admin.audit.action.songRestore': 'Song restored',
  'admin.audit.action.sessionRoomMoved': 'Room moved',
  'admin.audit.action.sessionScopeRefused': 'Refused: another venue',

  // MVP-358: the trash for the KJ app. `deleted` and `hidden` live on one list rather than on two
  // screens: an admin comes here asking "songs that are not in the catalogue right now", and one
  // list truer to the question is worth more than two lists split by which button caused it.
  'admin.withheld.title': 'Withheld songs',
  'admin.withheld.description': 'Songs a person took out of the catalogue — deleted or hidden. Restore any one to put it back.',
  'admin.withheld.refresh': 'Refresh',
  'admin.withheld.emptyTitle': 'Nothing withheld',
  'admin.withheld.emptyDetail': 'Every song in the catalogue is currently reachable from search.',
  'admin.withheld.loadError': 'Could not load the withheld list.',
  // A row lists the song's title, artist (if any), and one badge. Two labels, one for each of the
  // two states, rather than one label parameterised by status — a badge that says "Deleted" is a
  // different word from "Hidden" in every language this app ships with.
  'admin.withheld.badge.deleted': 'Deleted',
  'admin.withheld.badge.hidden': 'Hidden',
  // The row's Restore button. Its aria-label carries the song title so screen readers announce
  // WHICH song is being restored (the visible label is just "Restore" for space).
  'admin.withheld.restore': 'Restore',
  'admin.withheld.restoreAria': 'Restore {title}',
  // MVP-437: a deleted song that was hidden before it was deleted goes back to hidden, not into
  // the catalogue. Said on the button so the admin knows before pressing, and again afterwards
  // because the row stays on the list instead of leaving it.
  'admin.withheld.restoreToHidden': 'Restore as hidden',
  'admin.withheld.restoreToHiddenAria': 'Restore {title} as hidden — it stays out of search',
  'admin.withheld.restoredToHidden':
    '“{title}” is back, but hidden — it was hidden before it was deleted, so it is still not in search. Restore it once more to put it in the catalogue.',
  'admin.withheld.restoring': 'Restoring…',
  'admin.withheld.restoreError': 'Could not restore this song.',
  // The metadata line under each row: who deleted it, and when. `by {actor}` is a fragment because
  // the caller may or may not have that info — see the composition in `AdminWithheldScreen.tsx`.
  'admin.withheld.deletedBy': 'Deleted by {actor}',
  'admin.withheld.deletedAt': 'Deleted {when}',
  // MVP-458: the heading over a day's cards is the formatted date itself — no key, because a date is
  // not a sentence. This one covers the rows the backend sent with no `updatedAt` at all, which
  // cannot be filed under any day and must not be filed under today's.
  'admin.withheld.dayUnknown': 'No date recorded',
  // MVP-443: the list pages as you scroll instead of stopping at a server cap, so there is no
  // "showing the first 500" line any more — there is no 501st song to be honest about.
  'admin.withheld.loadingMore': 'Loading more songs…',
  'admin.withheld.moreError': 'Could not load the next page.',
  // MVP-443 filters. Withheld, not deleted, in the captions: a hidden song was never deleted, and
  // the date filter covers both. The actor filter is the one that really does mean deleted, and it
  // says so, because choosing a name there drops every hidden song from the list.
  'admin.withheld.filters': 'Filter',
  'admin.withheld.filterFrom': 'Withheld from',
  'admin.withheld.filterTo': 'Withheld until',
  // MVP-458: the search box. The hint names the three fields it actually matches, because a search
  // that silently does not look at the catalogue code is one an admin stops trusting.
  'admin.withheld.filterSearch': 'Search',
  'admin.withheld.filterSearchHint': 'Title, artist or code',
  'admin.withheld.filterActor': 'Deleted by',
  'admin.withheld.filterActorAny': 'Anyone',
  'admin.withheld.filterClear': 'Clear filters',
  'admin.withheld.filterEmptyTitle': 'No songs match this filter',
  'admin.withheld.filterEmptyDetail': 'Widen the dates, or clear the filters to see every withheld song.',
  // MVP-458: its own key rather than reusing `library.backToTop`, whose copy names the first SONG.
  // This list is grouped by day and the thing you go back to is the newest day, not a song.
  'admin.withheld.backToTop': 'Back to the top',

  'admin.audit.signedOut': 'signed out',
  'admin.audit.anAccount': 'an account',
  // One key per action. The labelled variants are whole sentences rather than one sentence with an
  // optional "({label})" tail: an invite's note is a phrase an admin typed, and where it sits in
  // the sentence is a decision each language makes for itself.
  'admin.audit.inviteCreate': 'Issued an invite link for {role}',
  'admin.audit.inviteCreateLabelled': 'Issued an invite link for {role} ({label})',
  'admin.audit.inviteRevoke': 'Revoked an invite link for {role}',
  'admin.audit.inviteRevokeLabelled': 'Revoked an invite link for {role} ({label})',
  'admin.audit.inviteRedeem': 'Created their account from an invite as {role}',
  'admin.audit.accountUpdate': 'Changed {target}: {changes}',
  'admin.audit.accountUpdateUnnamed': 'Changed an account: {changes}',
  'admin.audit.change': '{field} {from} → {to}',
  'admin.audit.changeNothing': 'nothing',
  'admin.audit.loginSuccess': 'Signed in',
  'admin.audit.loginRefused': 'Refused: {reason}',
  'admin.audit.refusedNotYetValid': 'their access has not started yet',
  'admin.audit.refusedExpired': 'their access has expired',
  'admin.audit.refusedOutsideWindow': 'outside their access window',
  'admin.audit.loginLocked': 'Too many sign-in attempts — locked out',
  'admin.audit.loginLockedFor': 'Too many sign-in attempts for "{target}" — locked out',
  'admin.audit.joinCodeRotated': 'Changed the join code',
  'admin.audit.sessionRoomMoved': 'Moved “{target}” from room {fromRoom} to room {toRoom}',
  'admin.audit.sessionUnnamed': 'this session',
  'admin.audit.sessionScopeRefused':
    'Was refused a session belonging to another venue ({route}, session {sessionId})',
  'admin.audit.joinCodeRotatedFor': 'Changed the join code for {target}',
  'admin.audit.passwordChange': 'Changed their password',
  // The account fields an audit row can name, and the two shapes their values take that are not
  // just the value itself.
  'admin.audit.field.roleType': 'role',
  'admin.audit.field.blocked': 'blocked',
  'admin.audit.field.displayName': 'name',
  'admin.audit.field.validFrom': 'access from',
  'admin.audit.field.validUntil': 'access until',
  'admin.audit.value.none': 'none',
  'admin.audit.value.blank': 'blank',
  'admin.audit.value.yes': 'yes',
  'admin.audit.value.no': 'no',

  // The roles an invite can grant, and what each one may do. Named by `INVITABLE_ROLES`.
  'admin.role.customer': 'Customer',
  'admin.role.customerHint': 'Runs a room: library, cockpit, rotation, settings.',
  'admin.role.shop': 'Shop',
  'admin.role.shopHint': 'Runs a room, and can invite its own staff. No downloads.',
  'admin.role.agency': 'Agency',
  'admin.role.agencyHint': 'Runs a room. Cannot invite anyone, cannot download.',
  'admin.role.downloader': 'Downloader',
  'admin.role.downloaderHint': 'Read-only catalogue and downloads. No queue or playback.',
  'admin.role.admin': 'Admin',
  'admin.role.adminHint': 'Everything a Customer can do, plus ingest and these accounts.',
  // MVP-585. Not an operator role: a rights agent reads one report and has no room, queue or
  // library at all.
  'admin.role.rights': 'Rights agent',
  'admin.role.rightsHint': 'Reads the royalties report only. Totals across shops, never one shop.',

  // MVP-585: the rights-agent report itself.
  'rights.report.title': 'Royalties report',
  'rights.report.period': 'Month',
  'rights.report.belowThreshold': 'Below reporting threshold (combined)',
  'rights.report.empty': 'No plays were reported for this month.',

  // An account's access window, in the tense that is true right now (`windowLabel`).
  'admin.window.permanent': 'Permanent',
  'admin.window.until': 'Until {date}',
  'admin.window.from': 'From {date}',
  'admin.window.expired': 'Expired {date}',

  'admin.invite.statusUsed': 'Used by {name}',
  'admin.invite.statusUsedUnknown': 'Used',
  'admin.invite.statusRevoked': 'Revoked',
  'admin.invite.statusExpired': 'Link expired',
  'admin.invite.statusActive': 'Active',
  'admin.invite.statusValidUntil': 'Link valid until {date}',

  'admin.invite.linksTitle': 'Invite links',
  // MVP-418: both lists carry column headers — "ยกเลิกแล้ว" in an unlabelled column is a word with
  // no question attached to it. (MVP-419 moved the panel counts onto the tabs, where they are bare
  // numbers beside the tab's own word and need no sentence of their own.)
  'admin.invite.colLink': 'Link',
  'admin.invite.colStatus': 'Status',
  'admin.invite.colGrants': 'Grants',
  'admin.invite.emptyTitle': 'No invite links yet',
  'admin.invite.emptyDetail': 'Create one above, then send it to the person joining.',
  'admin.invite.roleLabel': 'Role for this invite',
  // MVP-655: shown only for a Shop invite, where the branch is not a detail but the thing being
  // handed over. "Branch", not "venue": the rest of this UI already calls a venue a shop and its
  // rooms rooms, and a third word for the same object is how a screen stops being readable.
  'admin.invite.venueLabel': 'Branch this shop runs',
  'admin.invite.venueUnset': 'Choose a branch…',
  'admin.invite.noteLabel': 'Note (optional)',
  'admin.invite.notePlaceholder': 'e.g. Room 3',
  // MVP-465: the window's lower bound. Blank means "from the moment it is redeemed", which is what
  // every invite meant before this field existed.
  'admin.invite.accessFrom': 'Access from',
  'admin.invite.accessUntil': 'Access until',
  // MVP-465: replaces "leave the date blank" as the way to say it. An empty field cannot tell an
  // unfilled form from a deliberate decision, and this is the one field where that mistake grants
  // access that never lapses.
  'admin.invite.permanent': 'Never expires',
  'admin.invite.create': 'Create invite link',
  'admin.invite.formHint':
    '{roleHint} Leave "Access from" blank to start the moment the link is redeemed, and tick "Never expires" for access with no end date. The link itself stops working after 14 days whether or not it is used.',
  'admin.invite.oneTimeNotice': 'Send this link — it is shown once and cannot be looked up again:',
  'admin.invite.copy': 'Copy',
  'admin.invite.copied': 'Copied',
  // The window an invite GRANTS, as opposed to how long the link itself lasts. One sentence with
  // the window inside it, rather than a label glued to a lower-cased `windowLabel` — that trick
  // only reads as a sentence in a language that has cases at all.
  'admin.invite.grants': 'Grants: {window}',
  'admin.invite.revoke': 'Revoke',
  // MVP-324: Revoke used to fire straight from the ghost button. `target` is the same fallback
  // `inviteTarget` already computes below (the invite's own label, else the role it grants) — the
  // confirm names the exact link an admin is about to kill, not a generic "this invite".
  'admin.invite.revokeConfirmTitle': 'Revoke the invite for {target}?',
  'admin.invite.revokeConfirmDetail': 'The link will stop working immediately, even if it has not been used yet.',
  'admin.invite.createError': 'Could not create the invite.',
  'admin.invite.revokeError': 'Could not revoke that link.',
  // MVP-326: `target` is the invite's own note ("Room 3") when it has one, else the role it grants
  // — the same fallback `InviteRow` already uses to decide what identifies a row on this screen.
  'admin.invite.toastRevoked': 'Revoked the invite for {target}',

  'admin.accounts.title': 'Accounts',
  'admin.accounts.emptyTitle': 'No accounts',
  'admin.accounts.emptyDetail': 'Nobody has redeemed an invite yet.',
  'admin.accounts.you': 'you',
  // MVP-419: the tablist's accessible name — what these two tabs are tabs OF.
  'admin.accounts.tabsLabel': 'People and invites',
  // MVP-418
  'admin.accounts.blockedBadge': 'Blocked',
  'admin.accounts.editWindow': 'Change dates',
  'admin.accounts.colUser': 'Account',
  'admin.accounts.colRole': 'Role',
  'admin.accounts.colAccess': 'Access',
  'admin.accounts.roleLabel': 'Role for {name}',
  'admin.accounts.from': 'From',
  'admin.accounts.until': 'Until',
  'admin.accounts.save': 'Save',
  'admin.accounts.block': 'Block',
  'admin.accounts.unblock': 'Unblock',
  // MVP-324: Block/Unblock used to fire straight from the ghost button. `name` is the same
  // `primaryIdentity` the row already renders (displayName, falling back to username) — the confirm
  // names the exact account, not a generic "Are you sure?".
  'admin.accounts.blockConfirmTitle': 'Block {name}?',
  'admin.accounts.blockConfirmDetail': '{name} will not be able to sign in until you unblock them.',
  'admin.accounts.unblockConfirmTitle': 'Unblock {name}?',
  'admin.accounts.unblockConfirmDetail': '{name} will be able to sign in again.',
  // MVP-510 slice 4: the staff roster — a venue's own people, which is the roster a `shop` gets
  // where an admin gets the whole deployment's. Every other word on the panel (Role, Access, Save,
  // Block, the two confirmations, the toasts) is shared with `admin.accounts.*` on purpose: it is
  // the same question about a narrower list, and two dictionaries for one panel is how a screen
  // ends up calling the same thing two names.
  'admin.staff.title': 'Staff',
  'admin.staff.emptyTitle': 'No staff accounts',
  'admin.staff.emptyDetail': 'Nobody has redeemed one of your invite links yet.',
  'admin.staff.colBranch': 'Branch',
  'admin.accounts.loadError': 'Could not load accounts.',
  'admin.accounts.saveError': 'Could not save that change.',

  // MVP-326: the success toasts for `AccountRow`'s `save()`. That one function now commits role,
  // validFrom and validUntil together (MVP-325), so there are three shapes of "what changed" to
  // name rather than one generic "Saved" — block/unblock always arrives alone, but role and window
  // can land in the same request when an operator edits both before clicking Save once.
  'admin.accounts.toastRoleChanged': "Set {name}'s role to {role}",
  'admin.accounts.toastWindowChanged': "Saved {name}'s access window",
  'admin.accounts.toastRoleAndWindowChanged': "Updated {name}'s role and access window",
  'admin.accounts.toastBlocked': 'Blocked {name}',
  'admin.accounts.toastUnblocked': 'Unblocked {name}',

  // The ingest screen (MVP-225). File names, the catalogue code and the .mp4 extension are the
  // operator's own data and pass through untranslated.
  'admin.ingest.choose': 'Choose files…',
  'admin.ingest.uploading': 'Uploading…',
  'admin.ingest.uploadOne': 'Upload 1 file',
  'admin.ingest.uploadMany': 'Upload {count} files',
  'admin.ingest.clear': 'Clear list',
  'admin.ingest.emptyTitle': 'No files picked yet',
  'admin.ingest.emptyDetail':
    'Choose one or more video files. Leave the code blank and the next catalogue number is assigned automatically. For a whole folder, use the ingest CLI instead. It also measures loudness and trims trailing silence.',
  'admin.ingest.codeLabel': 'Catalogue code for {file}',
  'admin.ingest.codePlaceholder': 'auto',
  'admin.ingest.titleLabel': 'Title for {file}',
  'admin.ingest.titlePlaceholder': 'Title',
  'admin.ingest.artistLabel': 'Artist for {file}',
  'admin.ingest.artistPlaceholder': 'Artist',
  'admin.ingest.uploadingFile': 'Uploading {file}',
  'admin.ingest.summary': '{done} added.',
  'admin.ingest.summaryWithFailed': '{done} added, {failed} failed.',
  'admin.ingest.status.waiting': 'Waiting',
  'admin.ingest.status.hashing': 'Hashing…',
  'admin.ingest.status.finishing': 'Finishing…',
  'admin.ingest.status.uploading': 'Uploading…',
  'admin.ingest.status.added': 'Added',
  'admin.ingest.status.addedAs': 'Added as {code}',
  'admin.ingest.status.failed': 'Failed',
  'admin.ingest.status.cancelled': 'Stopped before this one finished',
  'admin.ingest.needTitle': 'Give this file a title before uploading.',
  'admin.ingest.badCatalogId': 'A catalogue id may only contain letters, digits and dashes.',
  // MVP-317: Clear used to wipe every hand-typed title/artist/catalogue id with no way back — now
  // routed through AlertDialog (DESIGN.md §7: destructive confirmations name the object and the count).
  'admin.ingest.clearConfirmOne': 'Clear 1 file from the list?',
  'admin.ingest.clearConfirmMany': 'Clear {count} files from the list?',
  'admin.ingest.clearConfirmDetail': 'Any title, artist or catalogue code typed in will be lost.',
  // MVP-317's `uploadFailed` / `uploadFailedFallback` lived here. Both are gone: they existed to wrap
  // the caught error's own `message` in a sentence, and MVP-336 stopped putting that text in a
  // headline at all. It reaches the row as `IngestFailure.detail`, behind `showDetail`, while the
  // headline is one of the `fail.*` sentences below.

  // MVP-336: batch-level progress and stopping a run.
  'admin.ingest.stop': 'Stop',
  'admin.ingest.batchProgress': '{done} of {total} done · uploading {file}',
  'admin.ingest.summaryWithCancelled': '{done} added, {failed} failed, {cancelled} stopped.',
  // Not "What the server said": verified live, the commonest detail is the BROWSER's own decoder
  // message for a file that is not really a video, which never reached a server at all.
  'admin.ingest.showDetail': 'Technical details',

  // MVP-339: the heading over the end-of-batch "what to try" block. The lines under it are the
  // SAME `admin.ingest.fail.*` sentences the rows carry, one per KIND — see `ingestFailureSummary`.
  'admin.ingest.whatToTry': 'What to try',

  // MVP-336: one sentence per failure the operator can actually distinguish and act on. Deliberately
  // NOT one per server error code — the three integrity checks (part length, part digest, whole-file
  // sha256) all mean "re-upload it", and splitting them would be reporting our own plumbing.
  // Each says what happened and what to do next; the server's own words stay behind `showDetail`.
  'admin.ingest.fail.network': "Couldn't reach the server. Check the connection and upload again.",
  'admin.ingest.fail.denied': 'Your session no longer has permission to add songs. Sign in again.',
  'admin.ingest.fail.diskFull': 'The server has no room left for this file. Free up space, then upload again.',
  'admin.ingest.fail.rejectedSize': 'The server refused this file for being too big. Add it with the ingest CLI instead.',
  'admin.ingest.fail.corrupt': 'The file arrived damaged. Upload it again.',
  'admin.ingest.fail.storeFailed': "The server couldn't store the file. Try again in a moment.",
  'admin.ingest.fail.server': 'The server had a problem with this file. Try again in a moment.',
  'admin.ingest.fail.empty': 'This file is empty. Check the copy that made it, then pick it again.',
  'admin.ingest.fail.tooLarge': 'This file is over {limit} MB. Add it with the ingest CLI instead.',
  'admin.ingest.fail.unreadable': "This file can't be read as a video. Check it plays, then try again.",
  'admin.ingest.fail.unknown': "This file couldn't be added. Try again; if it keeps failing, send the details below along.",

  // `redeemFormError` lives in `features/admin/invites.ts` — the same three rules the server
  // enforces — but renders on the invite-redeem screen, which is not an admin surface. Its own
  // phase translates the rest of that screen; these four are the ones the admin module owns.
  // The same screen's one line about what the invite grants, which reads `INVITABLE_ROLES` and
  // `windowLabel` and so could not stay English once those became keys.
  'auth.redeem.joiningAs': 'Joining as {role}.',
  'auth.redeem.accessPermanent': 'Your access does not expire.',
  'auth.redeem.access': 'Access: {window}.',
  'auth.redeem.usernameTooShort': 'Choose a username of at least 3 characters.',
  'auth.redeem.emailInvalid': 'Enter a valid email address.',
  'auth.redeem.passwordTooShort': 'Choose a password of at least 10 characters.',
  'auth.redeem.passwordMismatch': 'The two passwords do not match.',

  // MVP-272 Phase 7 (the last surface: signing in, redeeming an invite, and your own account).
  // Read once by a person who has just been handed a link, or who is standing at a machine they
  // cannot get into — the two moments in this app where confusing copy has no way out.
  'common.serverUnreachableRetry': 'Could not reach the server. Check your connection and try again.',
  // Shared by both password fields this product has: `redeemFormError` and `passwordFormError`
  // enforce the same floor, and the two hints must not drift apart from each other or from it.
  'auth.passwordMinHint': 'At least {min} characters',

  // The logo's accessible name on the sign-in and invite screens. A registered company name, so
  // `messages.th.ts` carries it UNCHANGED — the entry exists because a screen reader still has to
  // announce something there, and a literal `alt=` would be invisible to the gate that keeps this
  // surface honest, not because the words differ.
  'brand.logoAlt': 'RMS Publishing',

  'auth.login.subtitle': "Sign in to run tonight's show.",
  'auth.login.identifier': 'Email or username',
  'auth.login.password': 'Password',
  'auth.login.submit': 'Sign in',
  // Deliberately does not say WHICH of the two was wrong — the server does not either, and an
  // error that distinguishes them tells an attacker which accounts exist.
  'auth.login.badCredentials': 'That email/username or password is not right. Try again.',
  // MVP-645. A refusal the server answered with that this build has no sentence for. It used to
  // borrow `common.serverUnreachableRetry`, which claimed the machine was offline when it had just
  // replied — so this one says the sign-in was refused, and points at a person rather than a retry.
  'auth.login.refused': 'The server would not let this account sign in. Ask the shop owner or whoever set up your account to check it.',
  // MVP-471. Not yet owner-reviewed.
  'auth.login.challengeRequired': 'After a few failed attempts, confirm you are not a robot below, then sign in again.',
  // MVP-645. `CHALLENGE_UNAVAILABLE` — the robot check itself could not run (not configured, or
  // Cloudflare unreachable from the server). Separate from `challengeRequired` because the widget
  // below may never load, and the person at the machine has done nothing wrong and can do nothing
  // about it: the fix is on the server, so the sentence says who to call.
  'auth.login.challengeUnavailable': 'The robot check is not working at the moment, so sign-in cannot go through. Wait a minute and try once more; if it keeps happening, call support.',
  // MVP-318: `App.tsx`'s `onUnauthorized` listener — the only explanation a signed-out operator
  // gets for landing back on this screen mid-shift. Used to be built as a finished sentence at the
  // call site and handed to `LoginDialog` as a raw string; the dialog now takes the key instead
  // (`reason?: MessageKey`) so it goes through `t()` like every other string on this screen.
  'auth.sessionExpired': 'Your session expired. Sign in again to keep going.',

  'auth.invite.subtitle': 'You have been invited. Choose how you sign in.',
  'auth.invite.subtitleDone': 'Your account is ready.',
  // Unknown, already used and revoked all come back as the same 404 on purpose (the server refuses
  // to be an oracle), so this one sentence has to cover all three honestly.
  'auth.invite.linkInvalid':
    'This invite link is not valid any more. It may have been used already, cancelled, or expired — ask whoever sent it for a new one.',
  'auth.invite.checkFailed': 'Could not check this invite link. Try again in a moment.',
  'auth.invite.checking': 'Checking this invite…',
  'auth.invite.created': 'Account “{name}” created. Sign in with it from now on.',
  'auth.invite.goToSignIn': 'Go to sign in',
  'auth.invite.createFailed': 'Could not create the account. Check your connection and try again.',
  'auth.invite.username': 'Username',
  'auth.invite.email': 'Email',
  'auth.invite.password': 'Password',
  'auth.invite.passwordAgain': 'Password again',
  'auth.invite.submit': 'Create my account',

  'account.title': 'Your account',
  'account.username': 'Username',
  'account.email': 'Email',
  'account.role': 'Role',
  'account.askAdmin': 'Ask an admin if your email, username or role needs to change.',
  'account.displayName': 'Display name',
  'account.displayNameSaved': 'Display name saved.',
  'account.saveDisplayName': 'Save display name',
  'account.saveFailed': 'Could not save. Check your connection and try again.',
  'account.password': 'Password',
  'account.passwordChanged': 'Password changed.',
  'account.currentPassword': 'Current password',
  'account.newPassword': 'New password',
  'account.newPasswordAgain': 'New password again',
  'account.changePassword': 'Change password',
  // `profile.ts`'s rules. The two numbers are interpolated rather than written into the sentence,
  // so the copy cannot disagree with `DISPLAY_NAME_MAX`/`PASSWORD_MIN`.
  'account.displayNameTooLong': 'Keep the display name to {max} characters or fewer.',
  'account.needCurrentPassword': 'Enter your current password.',
  'account.newPasswordTooShort': 'Choose a new password of at least {min} characters.',
  'account.passwordMismatch': 'The two new passwords do not match.',
  'account.passwordUnchanged': 'The new password is the same as the current one.',
  'account.wrongCurrentPassword': 'That current password is not right.',
  'account.tooManyAttempts': 'Too many attempts. Wait a minute and try again.',
  // The two role names that are not invitable, and so are not in `admin.role.*`: the signed-out
  // state, and a guest at a table with no account at all.
  'account.role.guest': 'Guest',
  'account.role.signedOut': 'Signed out',

  // MVP-289. Six accessible names that lived hardcoded inside `ui/` primitives since before
  // MVP-272 existed — `Dialog`, `InlineAlert`, `Toast`, `Spinner` and `SidebarRail` all render
  // under every surface in the app, so a KJ or guest on the Thai UI still heard "Close"/"Dismiss"/
  // "Loading" out of a screen reader no matter which phase had translated the sentence around it.
  // `common.` because each of these is the same bare word repeated verbatim across guest, stage,
  // cockpit, library, settings and admin — never a surface-specific phrase.
  'common.close': 'Close',
  // MVP-424: `NumberStepper`'s +/- accessible names. `common.`, not a per-control pair, because
  // twelve call sites needed them and ten had none — the English template literal they fell back to
  // was announcing "Decrease จำนวนเพลงต่อรอบ" on a Thai-default screen.
  'common.decrease': 'Decrease {label}',
  'common.increase': 'Increase {label}',
  'common.dismiss': 'Dismiss',
  'common.loading': 'Loading',
  'common.mainNavigation': 'Main navigation',
  // MVP-357: `SearchField`'s clear button. `common.` rather than `library.search.clear`
  // because it names the same bare action on any field that has one — the guest page's own
  // `guest.search.clear` predates this and stays where it is, since it sits in a block that
  // ships to a separate bundle.
  'common.clearSearch': 'Clear search',
  // MVP-354. `rotation.undo` is a different word doing a different job — it undoes the LAST
  // rotation move, from a toolbar, whenever the operator likes. This one is the bare word on a
  // toast that undoes the specific thing that toast is reporting, which is why it is `common.`:
  // any surface that shows an undoable toast wants exactly this word.
  'common.undo': 'Undo',
  // MVP-497. Not yet owner-reviewed. `common.` because Commit B's guest half reuses these verbatim
  // and adds no copy of its own. NOT `ops.video.fullscreen` — that one says "Fullscreen video", and
  // this button is about the whole screen, not a player.
  'common.fullscreen.enter': 'Fullscreen',
  'common.fullscreen.exit': 'Exit fullscreen',

  // MVP-371. What the UI says when this product's own backend refuses something by code. These are
  // this app's sentences, not the wire's: `api/errorMessage.ts` matches `ApiError.code` against the
  // codes `strapi-template/src/api/**` sends and resolves the match here, so a Thai-default UI
  // never shows the server's English envelope text (least of all the users-permissions plugin's,
  // which this product did not write). A code with no entry here degrades to the caller's own
  // fallback sentence.
  'account.passwordChangeFailed': "That password change didn't go through — try again.",
  // MVP-390. Raised by api/client.ts, not by the backend — `fetch` never reached a server. The two
  // are split because the next step differs: check the connection, or wait for the machine. {host}
  // is the failing host, the same one DESIGN.md 6.4's offline indicator names.
  'server.offline': 'Could not reach {host}. Check your connection and try again.',
  'server.unreachable': "The server at {host} isn't responding (it may be restarting). Try again in a moment.",
  'server.authRequired': 'Sign in again to do that.',
  'server.rateLimited': 'Too many tries in a row — wait a moment and try again.',
  'server.sessionNotFound': "That session isn't live anymore.",
  // MVP-567. A different condition from the line above: that one is a session that has ended, this
  // one is a room that never opened tonight. Says the fix, and says it is this room's own session.
  'server.noActiveSession': "This room has no session open. Start this room's session, then try again.",
  'server.nothingToUndo': 'Nothing left to undo.',
  'server.nothingToRedo': 'Nothing left to redo.',
  'server.nothingToMerge': 'Pick a song to keep, and at least one other in the group to merge into it.',
  'server.patchEmpty': 'Nothing was changed.',
  'server.dateInvalid': "That date isn't valid.",
  'server.phaseInvalid': "That isn't a phase this session can be in.",
  'server.requestAlreadyDecided': 'Someone already decided this request.',
  'server.requestNotRestorable': 'Only a rejected request can be restored.',
  'server.requestTooOldToRestore': 'This request is too old to restore — it would expire straight away.',
  'server.requestsClosed': 'Requests are closed for tonight.',
  'server.requestsFull': "Tonight's request list is full.",
  'server.songAlreadyDeleted': 'That song is already in the bin.',
  'server.songNotDeleted': 'That song is not in the bin.',
  'server.songRequired': 'Pick a song first.',
  'server.inviteInvalid': 'This invite link is not valid.',
  'server.inviteExpired': 'This invite link has expired — ask for a new one.',
  // MVP-644. Every link issued before MVP-510 slice 1 shipped carries no shop, and the backend
  // refuses to redeem one rather than guess — guessing landed the account on a shop it had nothing
  // to do with. Distinct from `inviteInvalid`/`inviteExpired`: the link is neither wrong nor stale,
  // it is simply older than the rule, and the only fix is a fresh link from whoever sent it.
  'server.inviteNoVenue': 'This invite link was made before accounts were tied to a shop, so it can no longer be used. Ask whoever sent it for a new link.',
  'server.userExists': 'That username or email is already taken.',
  'server.usernameInvalid': 'Choose a username of at least 3 characters.',
  'server.emailInvalid': 'Enter a valid email address.',
  'server.passwordWeak': 'Choose a password of at least 10 characters.',
  'server.roleMissing': 'Pick a role for this account.',
  'server.roleInvalid': 'That role no longer exists on this server.',
  'server.selfLockout': 'That change would lock you out of your own admin account.',
  // MVP-508. The room controller's own two refusals. `ROOM_CAP_REACHED` deliberately does NOT
  // suggest closing a room: the server counts closed rooms too, so closing one frees nothing.
  'server.roomCapReached': 'This shop already has every room its licence covers. Closing a room does not free a slot — ask for a bigger package.',
  'server.venueNotInScope': 'This account reaches more than one shop, and this screen cannot yet say which one. Ask an admin to add the room.',
  // MVP-655. A shop invite hands over a branch, so it has to say which one. No fallback: the branch
  // this deployment already trades in is precisely what must not be handed to a stranger.
  'server.venueRequired': 'Choose the branch this shop will run before creating the link.',
  // MVP-635. The server's own text for this one names a route (`POST /api/rooms`); a KJ needs the
  // person, not the endpoint. Short on purpose — it is read on a phone, mid-shift.
  'server.venueHasNoRoom': 'This shop has no room yet, so there is nowhere to play. Ask the shop owner or an admin to open the first room.',
  // MVP-510 slice 3. Names the shop owner, because they are the only person who can reopen the
  // branch — a KJ reading this has nothing to retry.
  'server.venueNotTrading': 'This shop is marked as no longer trading, so its staff cannot sign in. Ask the shop owner or an admin to reopen it.',
  // MVP-636. Says the shop WAS created, because it was — the generic "could not save that change"
  // read as "nothing happened" and invited a second attempt. The cause stays in the server log.
  'server.venueRoomNotCreated': 'The shop was created, but its first room was not. Nobody can play in this shop until it has a room. Ask an admin to open the first room.',

  // MVP-508: the venue owner's own room roster.
  'nav.rooms': 'Rooms',
  'rooms.title': 'Rooms in this shop',
  'rooms.quota': 'Using {used} of {max} rooms',
  /** Shown instead of `rooms.quota` while the licence cap is unknown — see `roomRoster.ts`'s
   * `roomQuota`. A count with no cap, rather than a cap this app has invented. */
  'rooms.quotaCount': '{used} rooms',
  'rooms.quotaFull': 'Every room the licence covers is already open.',
  'rooms.add': 'Add room',
  'rooms.addTitle': 'Add a room',
  'rooms.name': 'Room name',
  'rooms.nameRequired': 'Give the room a name.',
  'rooms.nameTooLong': 'A room name can be at most {max} characters.',
  'rooms.rename': 'Rename',
  'rooms.renameTitle': 'Rename {room}',
  'rooms.save': 'Save',
  'rooms.close': 'Close room',
  'rooms.reopen': 'Reopen',
  'rooms.open': 'Open',
  'rooms.closed': 'Closed',
  'rooms.closeConfirm': 'Close {room}? It stops appearing in the room picker. Nothing is deleted, and you can reopen it here.',
  'rooms.empty': 'No rooms yet',
  'rooms.emptyDetail': 'Add the first room to this shop.',
  'rooms.loadError': 'Could not load this shop’s rooms.',
  'rooms.saveError': 'Could not save that change.',

  // MVP-509. The venue controller's own two refusals, neither of which had a sentence before.
  'server.venueCapReached':
    'This customer already has every branch their package covers. A branch that has stopped trading still holds its slot, so deactivating one frees nothing — raise the branch allowance on the licence.',
  'server.venueAdminOnly': 'Only an admin may open or change a branch.',
  // MVP-641. One sentence for both of the server's two: the body named no licence, and the body
  // named one that does not exist. `VENUE_HAS_NO_LICENCE` is a single code carrying no flag to tell
  // them apart (its `details` holds the code and nothing else), so the screen cannot distinguish
  // them without a backend change — and both have the same fix on this form, which is to pick the
  // customer from the list beside it. Says the branch was NOT created, because it was not.
  'server.venueHasNoLicence':
    'This branch was not created: no licence was chosen for it, or the one chosen no longer exists. Pick the customer from the licence list and save again. How many branches a customer may open comes from their package, so a branch under no licence is capped by nothing and billed to nobody.',
  // MVP-570: the room-count route's own refusal. A different sentence from the one above because it
  // is a different action — nothing was being written.
  'server.venueCountsAdminOnly': 'Only an admin may count the rooms of every branch.',

  // MVP-645. The two account-window refusals, read by somebody standing at a venue PC who has just
  // typed the RIGHT password. Each names the condition and the one person who can clear it; neither
  // says "try again", because trying again is the one thing that cannot work. No date is shown — the
  // server sends `validFrom` as an ISO timestamp inside its English sentence, and this map keys on
  // the code alone, so there is nothing to interpolate that a KJ would want to read.
  'server.accountNotYetValid': 'Your password is right, but this account has not been switched on yet. Ask the shop owner or whoever set it up to start it.',
  'server.accountExpired': 'Your password is right, but this account has run out. Ask the shop owner or whoever set it up to extend it.',

  // MVP-646 (a). Two sentences for the two codes the backend keeps apart on purpose: the room is
  // closed, or the whole branch is. Each names the thing that is actually deactivated, because an
  // operator looking at an open room will otherwise toggle the room checkbox all evening.
  'server.roomClosed': 'This room is closed, so a night cannot start in it. Reopen it on the Rooms screen first.',
  'server.venueClosed': 'This whole branch is deactivated, not just this room, so no night can start in it. Only an admin can turn the branch back on.',
  // MVP-646 (b). Reads as the `{reason}` tail of `ops.request.unacceptFailed`, like its three
  // siblings. The first half is the fact the KJ would otherwise never learn — the song is STILL in
  // the queue — and the second is the workaround, which is the queue column.
  'server.queueEntryNotRemovable': 'the song could not be taken out of the queue, so it is still there and still going to play — remove it from the queue column instead',
  // MVP-648 (b). Same tail shape. Somebody else deleted the line while this screen was showing it;
  // the panel reloads by itself, so the sentence says what to do with the list rather than "retry".
  'server.queueRowGone': 'that line is no longer in the list — someone else removed it. The list has just been refreshed',

  // MVP-648 (a). `nextRoomSlug` ran out of free names under this branch's prefix. The fix is on the
  // Rooms screen and nowhere else, so it is stated as the whole sentence.
  'server.roomSlugUnavailable': 'There is no free room name left under this branch. Rename an old room you no longer use, then add this one again.',

  // MVP-509: the vendor's own screen — customers, their packages, and their branches.
  'nav.licences': 'Licences',
  'licences.title': 'Licences and branches',
  'licences.rosterLabel': 'Customers',
  'licences.detailLabel': 'The selected customer',
  /** The banner. Three states, from `actingOnBanner` — never a branch without the customer above
   * it, because "branch 2" alone is the sentence this screen exists to stop anyone acting on. */
  'licences.actingOnLabel': 'Acting on',
  'licences.actingOn.none': 'Nothing selected — pick a customer on the left.',
  'licences.actingOn.licence': '{customer} — the whole licence',
  'licences.actingOn.venue': '{customer} → {venue} ({slug})',
  'licences.add': 'Add licence',
  'licences.addTitle': 'Add a licence',
  'licences.edit': 'Edit licence',
  'licences.editTitle': 'Edit {customer}',
  'licences.save': 'Save',
  'licences.customerName': 'Customer name',
  'licences.customerNameRequired': 'Give the customer a name.',
  'licences.customerNameTooLong': 'A customer name can be at most {max} characters.',
  'licences.slug': 'Identifier (slug)',
  'licences.slugRequired': 'Give this an identifier.',
  'licences.slugTooLong': 'An identifier can be at most {max} characters.',
  'licences.slugInvalid': 'Use lowercase letters, digits and hyphens only — for example “siam-karaoke”.',
  'licences.tier': 'Package',
  'licences.tierNone': 'No package named',
  'licences.maxVenues': 'Branches allowed',
  'licences.maxRoomsPerVenue': 'Rooms allowed per branch',
  'licences.capNone': 'No limit set',
  'licences.numberInvalid': 'Enter a whole number, or leave the box empty for no limit.',
  'licences.dateInvalid': 'Enter a date as YYYY-MM-DD, or leave the box empty for no expiry.',
  'licences.expiresAt': 'Expires',
  'licences.state': 'Status',
  'licences.stateActive': 'Active',
  'licences.stateInactive': 'Switched off',
  /** MVP-513 shipped the enforcement, so this screen describes the real rule rather than MVP-509's
   * own stale sentence about `expiresAt` being shown and not acted on. */
  'licences.expiryRule':
    'From 7 days before this date the shop sees a daily countdown. Service continues on the date itself and for 7 days after it. After that, no new night can be started — renewing (typing a later date) restores service by itself.',
  'licences.expiryNone': 'No expiry recorded',
  'licences.expiryOk': 'Runs for another {days} days',
  'licences.expiryEnding': 'Ends in {days} days',
  'licences.expiryGrace': 'Expired {days} days ago — still being served',
  'licences.expiryLapsed': 'Expired — no new nights can be started',
  /** Why there is no notes box. `licence.notes` and `venue.notes` are both `"private": true`, so no
   * content-API response carries them for any role, including admin. */
  'licences.notesElsewhere':
    'The private note on this customer is not shown here — it never leaves the Strapi admin panel, which is where you read and write it.',
  'licences.venues': 'Branches',
  'licences.venueQuota': 'Using {used} of {max} branches',
  'licences.venueQuotaCount': '{used} branches',
  'licences.venueQuotaFull': 'Every branch the package covers is already open.',
  'licences.addVenue': 'Add branch',
  'licences.addVenueTitle': 'Add a branch for {customer}',
  'licences.venueName': 'Branch name',
  'licences.venueNameRequired': 'Give the branch a name.',
  'licences.venueNameTooLong': 'A branch name can be at most {max} characters.',
  'licences.venueSlug': 'Branch identifier (slug)',
  'licences.venueSlugFixed':
    'Typed once, here. It cannot be changed afterwards: it is what every screen means by “which branch”, and each of the branch’s rooms carries it as a prefix.',
  'licences.venueOpen': 'Trading',
  'licences.venueClosed': 'Stopped',
  'licences.selectVenue': 'Select branch',
  'licences.deactivateVenue': 'Stop this branch',
  'licences.reactivateVenue': 'Let this branch trade again',

  'licences.reps': 'Reps who look after this branch',
  'licences.repsNone': 'No rep — we opened this branch ourselves',
  'licences.repsAdd': '+ Add a rep',
  'licences.repsRemove': 'Remove {name} from this branch',
  'licences.repsNoAccounts': 'There are no rep accounts yet',
  'licences.repsAllAssigned': 'Every rep is already on this branch',
  'licences.repsHint': 'Handing a branch on means adding a name, not replacing one. Everyone listed can see this branch; remove somebody to take that away.',
  'licences.deactivateVenueConfirm':
    'Stop {venue} ({slug})? No new night can be started there until it is switched back on. Nothing is deleted — its rooms, accounts and history all stay, and you can reverse this here.',
  /** MVP-570: the two `unknown` sentences. Since slice 3 the used count IS served — but a VPS that
   * predates that deploy 404s the route, and the row still has to say so rather than show a zero: a
   * blank or a zero would both read as a number somebody would act on. */
  'licences.venueRoomsCapOnly': 'Up to {max} rooms — the number in use is not available on this screen',
  'licences.venueRoomsUnknown': 'No room limit set — the number in use is not available on this screen',
  /** MVP-570: used against the cap, and the two states that matter. `venueRoomsOver` names the
   * overage rather than merely flagging it — "over by 2" is the sentence a vendor repeats on the
   * phone — and both badges carry their own WORDS, because colour is never the only indicator. */
  'licences.venueRoomsUsed': '{used} of {max} rooms',
  'licences.venueRoomsCount': '{used} rooms — no room limit set',
  'licences.venueRoomsFull': 'At the room limit',
  'licences.venueRoomsOver': 'Over the room limit by {over}',
  'licences.empty': 'No licences yet',
  'licences.emptyDetail': 'Add the first customer’s package.',
  'licences.noVenues': 'No branches yet',
  'licences.noVenuesDetail': 'Add this customer’s first branch.',
  'licences.selectPrompt': 'Pick a customer',
  'licences.selectPromptDetail': 'Choose one on the left to see their package and their branches.',
  'licences.loadError': 'Could not load the licences.',
  'licences.saveError': 'Could not save that change.',
  // MVP-278: self-serve plans: sign-up, the plans page, the slip queue, their audit rows.
  'nav.plans': 'Plans & renewal',
  'nav.payments': 'Check slips',
  'ops.licence.renew': 'Renew',
  'auth.login.signup': 'New shop? Sign up',
  'auth.signup.subtitle': 'Sign up your shop and start a free trial.',
  'auth.signup.shopName': 'Shop name',
  'auth.signup.phone': 'Phone number (optional)',
  'auth.signup.submit': 'Sign up and start the trial',
  'auth.signup.failed': 'Could not sign you up. Try again.',
  'auth.signup.challengeRequired': 'Confirm you are not a robot below, then press Sign up again.',
  'auth.signup.challengeUnavailable': 'The robot check is not available right now, so sign-up cannot go ahead. Wait a moment and try again.',
  'auth.signup.goToSignIn': 'Go to sign in',
  'auth.signup.haveAccount': 'Already have an account? Sign in',
  'server.shopNameInvalid': 'Enter the shop name, 2 to 120 characters.',
  'server.phoneInvalid': 'Enter a Thai phone number, or leave it empty.',
  'server.trialAlreadyUsed': 'This email or phone number has already had a free trial.',
  'server.trialUnavailable': 'Sign-up is closed right now. Please try again later.',
  'server.licenceRequired': 'This account has no licence to pay for.',
  'server.planNotFound': 'That plan is not on sale.',
  'server.planNotPurchasable': 'The free trial cannot be bought.',
  'server.planNotPriced': 'This plan is not on sale yet.',
  'server.slipRequired': 'Attach the transfer slip first.',
  'server.slipInvalid': 'The slip must be an image or a PDF.',
  'server.slipTooLarge': 'The slip file is too large. It must be 10 MB or smaller.',
  'server.slipDuplicate': 'This slip has already been sent. Wait for it to be checked.',
  'server.licenceNotSelfServe': 'This licence is renewed by your supplier, not here.',
  'server.emailNotConfirmed': 'Confirm your email first. Open the link we sent you, or send it again below.',
  'server.emailSendFailed': 'Your account was created, but the confirmation email could not be sent. Press "Send the email again".',
  'server.signupPending': 'This email is already signed up and waiting for confirmation. Check your inbox, or send the email again.',
  'server.confirmInvalid': 'This confirmation link does not work. It may have been used already. Try signing in.',
  'server.confirmExpired': 'This confirmation link has expired. Sign in with your email and password to get a new one.',
  'server.originUnknown': 'Sign-up must be done from the RMS Karaoke website.',
  'server.bankAccountIncomplete': 'Fill in the bank, the account name and the account number, or leave all three empty.',
  'server.bankAccountNoInvalid': 'An account number is 10 to 15 digits (dashes and spaces are fine).',
  'server.promptPayIdInvalid': 'A PromptPay ID is a 10-digit mobile number or a 13-digit tax ID.',
  'auth.checkEmail.title': 'Check your email',
  'auth.checkEmail.body': 'We sent a confirmation link to {email}. Open it within 24 hours to start your free trial.',
  'auth.checkEmail.resend': 'Send the email again',
  'auth.checkEmail.resent': 'Sent. If it does not arrive in a few minutes, look in the spam folder.',
  'auth.checkEmail.resendFailed': 'Could not send it. Try again in a moment.',
  'auth.confirm.checking': 'Confirming your email…',
  'auth.confirm.done': 'Your email is confirmed and your 7-day free trial has started. Sign in with your email and password.',
  'auth.confirm.failed': 'Could not confirm your email. Try again in a moment.',
  'server.signupClosed': 'Sign-up is not open right now.',
  'server.adminOnly': 'Only an admin can check slips.',
  'server.paymentIncomplete': 'This payment has no licence or plan to renew.',
  'server.paymentAlreadyDecided': 'This slip was already checked by someone else. The list has been reloaded.',
  'server.noteRequired': 'Give the reason the slip did not pass. The shop is shown it.',
  'plans.expires': 'Active until {date}',
  'plans.loadError': 'Could not load the plans.',
  'plans.days': '{days} days',
  'plans.limits': '{venues} branches, {rooms} rooms per branch',
  'plans.price': '{price} baht',
  'plans.notOnSale': 'Not on sale yet',
  'plans.trial': 'Free trial',
  'plans.choose': 'Choose this plan',
  'plans.pay.heading': '{plan}: transfer {price} by PromptPay',
  'plans.pay.steps': 'Scan the QR with your banking app, transfer exactly this amount, then attach the slip below.',
  'plans.pay.bankSteps': 'Transfer exactly this amount to the account below, then attach the slip.',
'plans.pay.bankName': 'Bank',
'plans.pay.bankAccountName': 'Account name',
'plans.pay.bankAccountNo': 'Account number',
'plans.pay.amount': 'Amount to transfer',
'plans.pay.copy': 'Copy',
'plans.pay.notConfigured': 'No receiving account is set up yet.',
  'plans.pay.slip': 'Transfer slip (image or PDF)',
  'plans.pay.submit': 'Send the slip',
  'plans.pay.sent': 'Slip sent. An admin will check it.',
  'plans.pay.failed': 'Could not send the slip.',
  'plans.history.title': 'Payment history',
  'plans.history.empty': 'No payments yet',
  'plans.history.emptyDetail': 'Choose a plan above to renew.',
  'plans.status.pending': 'Waiting for review',
  'plans.status.approved': 'Approved',
  'plans.status.rejected': 'Did not pass',
  'plans.reason': 'Reason: {note}',
  'payments.filter': 'Slip status',
  'payments.filterAll': 'All',
  'payments.loadError': 'Could not load the slips.',
  'payments.empty': 'Nothing here',
  'payments.emptyDetail': 'Slips the shops send appear here.',
  'payments.openSlip': 'Open the slip (PDF)',
  'payments.slipAlt': 'Transfer slip from {shop}',
  'payments.noSlip': 'No slip attached',
  'payments.viewSlip': 'View the slip',
  'payments.slipLoadError': 'Could not open the slip. Try again.',
  'payments.unknownShop': 'Unknown shop',
  'payments.sentAt': 'Sent {date}',
  'payments.expires': 'Licence ends {date}',
  'payments.note': 'Reason (required if it does not pass)',
  'payments.approve': 'Approve',
  'payments.reject': 'Does not pass',
  'payments.approved': 'Approved. The shop\'s licence has been renewed.',
  'payments.rejected': 'Saved as not passed. The shop will see the reason.',
  'payments.actionFailed': 'Could not save the check.',
  'payments.decidedBy': 'Checked by {name} on {date}',
  'admin.audit.action.signupTrial': 'Trial sign-up',
  'admin.audit.action.paymentApprove': 'Slip approved',
  'admin.audit.action.paymentReject': 'Slip did not pass',
  'admin.audit.action.paymentAccountUpdate': 'Payment account changed',
  'admin.audit.signupTrial': 'Signed up a new shop on the free trial ({target})',
  'admin.audit.paymentApprove': 'Approved a slip for {target}, licence now ends {to}',
  'admin.audit.paymentReject': 'Did not pass a slip for {target}: {note}',
  'admin.audit.paymentAccountUpdate': 'Changed the account shops pay into',
} as const;

/** Every later phase's dictionary keys are drawn from this union — never hand-maintained, so a
 * key added or renamed in `en` above is immediately known everywhere it is used. */
export type MessageKey = keyof typeof en;
